/*
 * TMB Merged Plan, v0.1
 *
 * Reads the ticked plans from localStorage (tmb_hub_plan_filter), fetches
 * each ticked plan's JSON in parallel via the same readPlan endpoint the
 * per-plan page uses, then renders a single calendar-by-week view that
 * looks like the per-athlete plan page but unioned across plans.
 *
 * v0.1 scope (read-only):
 *   - Header: "Plan, <name>"
 *   - Today panel: every ticked plan's today-sessions, color-coded by plan
 *   - Week strip: this week + next 11 weeks (by calendar date, not w-id)
 *   - Active-week day grid: sessions for the visible week, grouped by day
 *   - Session card click → navigates to source plan page (Stage B will
 *     swap this for an inline modal + tick/log writeback)
 */
(function () {
  'use strict';

  var root = document.querySelector('.tmb-merged[data-endpoint]');
  if (!root) return;
  var endpoint = root.getAttribute('data-endpoint') || '';
  var titleEl  = root.querySelector('[data-merged-title]');
  var subEl    = root.querySelector('[data-merged-sub]');
  var statusEl = root.querySelector('[data-merged-status]');
  var resultsEl = root.querySelector('[data-merged-results]');

  var EMAIL_KEY  = 'tmb_hub_email';
  var FILTER_KEY = 'tmb_hub_plan_filter';

  // Per-source-token plan data, kept around so the modal can read
  // exports / settings / viewer for tick state + writeback target.
  // Populated in boot_() after fetchPlanData_ completes for each plan.
  // (#109 v1 stage 1)
  var _planByToken_ = {};
  var DAY_CODES  = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var SLOT_ORDER = { 'am': 1, 'all day': 2, 'pm': 3, 'focus': 4 };

  function readSavedEmail_() {
    try { return localStorage.getItem(EMAIL_KEY) || ''; } catch (_e) { return ''; }
  }
  function readPlanFilter_() {
    try { return JSON.parse(localStorage.getItem(FILTER_KEY) || '{}'); }
    catch (_e) { return {}; }
  }
  function writePlanFilter_(filter) {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(filter || {})); }
    catch (_e) {}
  }
  function planIsIncluded_(token, filter) {
    if (!token) return true;
    var v = filter && filter[token];
    return v !== false;
  }
  function escapeHtml_(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr_(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function setStatus_(msg, kind) {
    if (!statusEl) return;
    statusEl.textContent = msg || '';
    statusEl.setAttribute('data-kind', kind || '');
  }

  // Stable pastel HSL per plan name (matches the hub's planColor_).
  function planColor_(name) {
    var s = String(name || '');
    if (__planColorByName_[s]) return __planColorByName_[s];
    var h = 0;
    for (var i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    var hue = Math.abs(h) % 360;
    // De-collide: hash collisions were giving several plans the same
    // colour. Nudge by golden-angle steps until this hue sits at least
    // 26 degrees from every hue already assigned this pageload.
    function clashes_(cand) {
      return __planHues_.some(function (u) {
        var d = Math.abs(cand - u) % 360;
        return Math.min(d, 360 - d) < 26;
      });
    }
    var guard = 0;
    while (clashes_(hue) && guard++ < 14) hue = (hue + 137) % 360;
    __planHues_.push(hue);
    __planColorByName_[s] = 'hsl(' + hue + ', 55%, 48%)';
    return __planColorByName_[s];
  }
  var __planHues_ = [];
  var __planColorByName_ = {};

  // Fetch plans a few at a time instead of all at once. Nine simultaneous
  // GETs against one Apps Script /exec trip its concurrent-invocation
  // limits and cold-start several of them into the 20s abort, which is
  // how "4 of 9 plans failed to load" happens. Three lanes keep the
  // server warm without the stampede, and one sequential retry pass
  // picks up stragglers once the container is hot.
  function fetchPlansLimited_(items, worker) {
    var results = new Array(items.length);
    var next = 0;
    function lane() {
      var i = next++;
      if (i >= items.length) return Promise.resolve();
      return worker(items[i], i).then(function (res) { results[i] = res; return lane(); });
    }
    var lanes = [];
    for (var k = 0; k < Math.min(3, items.length); k++) lanes.push(lane());
    return Promise.all(lanes).then(function () { return results; });
  }

  function tokenFromPageUrl_(pageUrl) {
    if (!pageUrl) return '';
    try {
      var u = new URL(pageUrl, window.location.origin);
      return u.searchParams.get('token') || '';
    } catch (_e) {
      var m = String(pageUrl).match(/[?&]token=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    }
  }

  function todayIso_() {
    var d = new Date();
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function isoDateAddDays_(iso, n) {
    var d = new Date(iso + 'T00:00:00');
    d = new Date(d.getTime() + n * 86400000);
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  // Monday-anchored week-start for a given ISO date.
  function weekStartFor_(iso) {
    var d = new Date(iso + 'T00:00:00');
    var dayIdx = d.getDay();         // 0=Sun … 6=Sat
    var diff = (dayIdx + 6) % 7;     // back to Monday
    d.setDate(d.getDate() - diff);
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  // For one plan's JSON, return every session's ISO date in the plan
  // window. Returns [{ iso, dayCode, session, sourceToken, planTitle, pageUrl }].
  // Uses Date math (not iso-string comparison) + a 90-day safety cap per
  // week so a malformed Start/End Date can't lock the browser in an
  // infinite loop.
  function expandPlanSessions_(plan, sourceMeta) {
    if (!plan || !plan.imports || !plan.weeks) return [];
    var weekByDate = {};
    plan.weeks.forEach(function (w) {
      var ws = String(w['Start Date'] || '').slice(0, 10);
      var we = String(w['End Date']   || '').slice(0, 10);
      if (ws.length !== 10 || we.length !== 10) return;
      var startMs = new Date(ws + 'T00:00:00').getTime();
      var endMs   = new Date(we + 'T00:00:00').getTime();
      if (!isFinite(startMs) || !isFinite(endMs) || endMs < startMs) return;
      var spanDays = Math.round((endMs - startMs) / 86400000);
      if (spanDays > 90) return;
      var weekId = String(w.Week || '');
      for (var i = 0; i <= spanDays; i++) {
        var d = new Date(startMs + i * 86400000);
        var iso = d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
        weekByDate[iso] = { weekId: weekId, dayCode: DAY_CODES[d.getDay()] };
      }
    });
    // Index imports by (weekId|dayCode) so per-iso lookup is O(1).
    var importsByKey = {};
    plan.imports.forEach(function (r) {
      var key = String(r.Week || '') + '|' + String(r.Day || '').toLowerCase();
      if (!importsByKey[key]) importsByKey[key] = [];
      importsByKey[key].push(r);
    });
    var out = [];
    Object.keys(weekByDate).forEach(function (iso) {
      var info = weekByDate[iso];
      var matches = importsByKey[info.weekId + '|' + info.dayCode] || [];
      matches.forEach(function (r) {
        out.push({
          iso: iso,
          dayCode: info.dayCode,
          session: r,
          sourceToken: sourceMeta.token,
          planTitle: sourceMeta.planTitle,
          pageUrl: sourceMeta.pageUrl,
          color: planColor_(sourceMeta.planTitle),
        });
      });
    });
    return out;
  }

  // Per-plan version + data cache (sessionStorage). Server-side
  // ?_seen= short-circuit means if nothing has changed since we last
  // fetched a plan, the next fetch comes back as a tiny notModified
  // body and we serve the cached data. Each plan has its own entry
  // keyed by token. Survives back-navigation within the tab.
  var PLAN_DATA_CACHE_KEY_ = 'tmb_merged_plan_data:';
  function readCachedPlanData_(token) {
    try {
      var raw = sessionStorage.getItem(PLAN_DATA_CACHE_KEY_ + token);
      if (!raw) return null;
      var c = JSON.parse(raw);
      return (c && c.version && c.data) ? c : null;
    } catch (_e) { return null; }
  }
  function writeCachedPlanData_(token, version, data) {
    try {
      sessionStorage.setItem(
        PLAN_DATA_CACHE_KEY_ + token,
        JSON.stringify({ version: version, data: data, at: Date.now() })
      );
    } catch (_e) { /* quota, fine, just refetches next time */ }
  }

  // Window the merged plan to the next N days across all plans. The
  // page only renders one day at a time (prev/next nav) so the long
  // history isn't visible anyway; cutting the payload here dramatically
  // shortens cold-start load. Server caches the FULL plan and applies
  // the filter per-request, so per-plan-page requests still hit cache.
  var MERGED_WINDOW_DAYS_ = 14;
  function fetchPlanData_(token, email) {
    if (!token) return Promise.resolve(null);
    var fromIso = todayIso_();
    // Cache key is per-day so yesterday's window doesn't leak forward.
    // ?_seen short-circuit can't be used here, same plan version may
    // produce different windows on different days.
    var cacheKey = token + ':' + fromIso + ':' + MERGED_WINDOW_DAYS_;
    var cached = readCachedPlanData_(cacheKey);
    var url = endpoint + '?token=' + encodeURIComponent(token) +
      '&days=' + MERGED_WINDOW_DAYS_ +
      '&fromIso=' + encodeURIComponent(fromIso) +
      '&_=' + Date.now();
    if (email) url += '&email=' + encodeURIComponent(email);
    var ctrl = null;
    try { ctrl = new AbortController(); } catch (_e) {}
    var opts = ctrl ? { method: 'GET', signal: ctrl.signal } : { method: 'GET' };
    var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (_e) {} }, 20000) : null;
    return fetch(url, opts)
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (timer) clearTimeout(timer);
        if (!j || !j.ok) return cached && cached.data ? cached.data : null;
        if (j.planVersion) writeCachedPlanData_(cacheKey, String(j.planVersion), j.data);
        return j.data;
      })
      .catch(function (err) {
        if (timer) clearTimeout(timer);
        try { window.tmbLogError && window.tmbLogError('fetch_plan_data_merged', String(err && err.message || err), { stack: err && err.stack || '', planToken: token, notes: 'merged-plan per-plan fetch' }); } catch (_e) {}
        return cached && cached.data ? cached.data : null;
      });
  }

  // #109 v0.5, entry registry keyed by stable index so click handlers can
  // look up the full entry (with description, prescribed sets, source plan
  // meta) without serialising the world into a data attribute.
  var __entryRegistry_ = {};
  var __entryNextId_ = 0;
  function registerEntry_(entry) {
    var id = String(++__entryNextId_);
    __entryRegistry_[id] = entry;
    return id;
  }

  // Pick the first "workout meat" lines for the card preview. Skips
  // FOCUS / WARM-UP / PROGRESSION / ALTERNATIVES sections + dividers
  // + bare-empty lines, returns the first ~8 lines of the
  // STRENGTH/MAIN/CORE/etc block so the card teases what's inside.
  function describePreviewLines_(text) {
    if (!text) return [];
    var lines = String(text).replace(/\r\n/g, '\n').split('\n');
    var SKIP_SECTIONS = /^\s*\*?\*?(FOCUS|WARM-?UP|PROGRESSION|ALTERNATIVES|WEEK\s+TARGET)\b/i;
    var DIVIDER = /^\s*[─━‐─-]{6,}\s*$/;
    var inSkipBlock = false;
    var out = [];
    var preludeDone = false;  // become true once we've passed FOCUS+WARM-UP
    for (var i = 0; i < lines.length && out.length < 8; i++) {
      var line = lines[i];
      var trimmed = line.trim();
      if (!trimmed) continue;
      if (DIVIDER.test(trimmed)) {
        inSkipBlock = false;  // dividers end a block
        continue;
      }
      if (SKIP_SECTIONS.test(trimmed)) {
        inSkipBlock = true;
        preludeDone = true;
        continue;
      }
      if (inSkipBlock) continue;
      // Skip standalone working-set instructions (long paragraphs).
      if (/^For each main exercise below/i.test(trimmed)) continue;
      // Strip leading **bold** markers; the preview renders inline-bold
      // separately. Keep the text.
      var clean = trimmed.replace(/^\*\*(.+?)\*\*$/, '$1');
      out.push(clean);
    }
    // If we never found a non-prelude block (small descriptions), fall
    // back to first few meaningful lines after the FOCUS sentence.
    if (!out.length) {
      for (var j = 0; j < lines.length && out.length < 6; j++) {
        var t = lines[j].trim();
        if (!t || DIVIDER.test(t) || SKIP_SECTIONS.test(t)) continue;
        out.push(t.replace(/^\*\*(.+?)\*\*$/, '$1'));
      }
    }
    return out;
  }

  // Render a preview block, first line bold (it's typically a section
  // title or movement-block name), the rest as a quick read.
  function renderCardPreview_(text) {
    var lines = describePreviewLines_(text);
    if (!lines.length) return '';
    return '<span class="tmb-merged-card-preview">' +
      lines.map(function (l, i) {
        var inline = escapeHtml_(l).replace(/\*\*([^*][^*]*?)\*\*/g, '<strong>$1</strong>');
        // Render bullet-prefixed lines as items.
        if (/^\s*[•\-*]\s+/.test(l)) {
          return '<span class="tmb-merged-card-preview-bullet">' + inline.replace(/^\s*[•\-*]\s+/, '') + '</span>';
        }
        return '<span class="tmb-merged-card-preview-line' + (i === 0 ? ' is-first' : '') + '">' + inline + '</span>';
      }).join('') +
    '</span>';
  }

  function renderSessionCard_(entry, email) {
    var s = entry.session;
    var slot = String(s.Slot || '').trim();
    var titleStr = String(s.Name || s.Title || s.Category || 'Session');
    // Right-hand meta: slot + category + duration + priority on a single
    // dot-separated line. Saves vertical space vs. the old 3-row layout.
    var rmeta = [];
    if (slot && slot.toLowerCase() !== 'all day') rmeta.push(slot);
    if (s.Category) rmeta.push(String(s.Category));
    if (s['Planned (min)']) rmeta.push(s['Planned (min)'] + ' min');
    if (s.Priority) rmeta.push(String(s.Priority));
    var eid = registerEntry_(entry);
    var previewHtml = renderCardPreview_(s.Description);
    return '<button type="button" class="tmb-merged-card' + (previewHtml ? ' has-preview' : '') + '" ' +
      'data-merged-entry="' + eid + '" ' +
      'style="--plan-color: ' + entry.color + '">' +
      '<span class="tmb-merged-card-strip"></span>' +
      '<span class="tmb-merged-card-body">' +
        '<span class="tmb-merged-card-head">' +
          '<span class="tmb-merged-card-plan">' + escapeHtml_(entry.planTitle || 'Plan') + '</span>' +
          '<span class="tmb-merged-card-title">' + escapeHtml_(titleStr) + '</span>' +
          '<span class="tmb-merged-card-rmeta">' + escapeHtml_(rmeta.join(' · ')) + '</span>' +
        '</span>' +
        previewHtml +
      '</span>' +
    '</button>';
  }

  // Minimal description renderer, converts **bold** to <strong>, paragraph
  // breaks (double newline) to <p>, single newlines to <br>. Plain HTML
  // is escaped first so the source-of-truth description text can never
  // inject markup.
  // Section labels that should render as h4 red headings, mirroring
  // the per-plan training-plan-v2 renderer. Anything else in **bold**
  // stays as inline <strong> so movement names (e.g. "Spanish Squat
  // Isometric") don't get treated like top-level sections.
  var DESC_HEADING_RE_ = /^(?:FOCUS|WARM-?UP|STRENGTH|MAIN|CORE|CONDITIONING|FINISHER|COOL-?DOWN|PROGRESSION|ALTERNATIVES|WEEK TARGET|AMRAP|EMOM|TABATA|FOR\s+TIME|\d+\s+ROUNDS?\s+FOR\s+TIME|CORE\s+[—-]\s+\d+\s+ROUNDS?)(?:\s*[—:·–-].*)?$/i; // em-dash-ok: parses description headings STORED with dashes
  // Section names whose body wraps in <details> so the athlete can
  // collapse reference material that's not relevant every visit.
  // Mirrors COLLAPSIBLE_SECTIONS_ in training-plan-v2.js.
  var COLLAPSIBLE_SECTIONS_ = ['PROGRESSION', 'ALTERNATIVES'];
  function isCollapsibleHeading_(text) {
    var s = String(text || '').trim().toUpperCase();
    return COLLAPSIBLE_SECTIONS_.some(function (lbl) {
      return s === lbl || s.indexOf(lbl + ' ') === 0 || s.indexOf(lbl + ':') === 0;
    });
  }
  function renderDescription_(text) {
    if (!text) return '';
    var src = String(text).replace(/\r\n/g, '\n');
    var lines = src.split('\n');
    var html = [];
    var paraBuf = [];
    var listType = null;  // 'ul' | 'ol' | null
    var detailsOpen = false;
    function flushPara() {
      if (paraBuf.length) {
        html.push('<p>' + paraBuf.join('<br>') + '</p>');
        paraBuf = [];
      }
    }
    function flushList() {
      if (listType) { html.push('</' + listType + '>'); listType = null; }
    }
    function flushDetails() {
      if (detailsOpen) { html.push('</details>'); detailsOpen = false; }
    }
    function inlineMd_(s) {
      // **bold** only, keep the inline renderer narrow on purpose.
      return s.replace(/\*\*([^*][^*]*?)\*\*/g, '<strong>$1</strong>');
    }
    lines.forEach(function (raw) {
      var line = String(raw || '');
      var trimmed = line.trim();
      // Skip the box-drawing divider lines used between sections.
      if (/^\s*[─━‐─-]{6,}\s*$/.test(trimmed)) {
        flushPara();
        flushList();
        flushDetails();
        html.push('<hr class="cm-desc-hr">');
        return;
      }
      // Blank line → paragraph break (also closes any open list).
      if (!trimmed) {
        flushPara();
        flushList();
        return;
      }
      // Section-name heading. Also handles `**HEADING**` form.
      var headingCandidate = trimmed.replace(/^\*\*(.+?)\*\*$/, '$1').trim();
      if (DESC_HEADING_RE_.test(headingCandidate)) {
        flushPara();
        flushList();
        flushDetails();
        if (isCollapsibleHeading_(headingCandidate)) {
          html.push('<details class="cm-desc-collapsible">' +
            '<summary class="cm-desc-section cm-desc-section--collapsible">' +
            escapeHtml_(headingCandidate) + '</summary>');
          detailsOpen = true;
        } else {
          html.push('<h4 class="cm-desc-section">' + escapeHtml_(headingCandidate) + '</h4>');
        }
        return;
      }
      // Bullet list item, `• `, `- `, or `* ` prefix.
      var bulletM = line.match(/^\s*[•\-*]\s+(.*)$/);
      // Numbered list item, `1. `, `2. ` etc.
      var numM = line.match(/^\s*\d+\.\s+(.*)$/);
      if (bulletM) {
        flushPara();
        if (listType !== 'ul') { flushList(); html.push('<ul>'); listType = 'ul'; }
        html.push('<li>' + inlineMd_(escapeHtml_(bulletM[1])) + '</li>');
        return;
      }
      if (numM) {
        flushPara();
        if (listType !== 'ol') { flushList(); html.push('<ol>'); listType = 'ol'; }
        html.push('<li>' + inlineMd_(escapeHtml_(numM[1])) + '</li>');
        return;
      }
      flushList();
      paraBuf.push(inlineMd_(escapeHtml_(line)));
    });
    flushPara();
    flushList();
    flushDetails();
    // Strip dividers that fall straight before a section heading or
    // collapsible, the heading is its own visual separator.
    return html.join('')
      .replace(/<hr class="cm-desc-hr">\s*<h4 class="cm-desc-section">/g, '<h4 class="cm-desc-section">')
      .replace(/<hr class="cm-desc-hr">\s*<details class="cm-desc-collapsible">/g, '<details class="cm-desc-collapsible">');
  }

  // WOD library lookup + prescription-card renderer (#93 Phase 3).
  // When an Import row has its `WOD ID` column set to a canonical WOD
  // id, the modal renders the library entry as a structured card
  // above the description: name + format badge + time cap + structure
  // + equipment chips. The Description stays for athlete-specific
  // notes (pacing cues, scaling suggestions).
  function findWodById_(plan, wodId) {
    if (!plan || !plan.wods || !wodId) return null;
    var q = String(wodId).trim().toLowerCase();
    if (!q) return null;
    for (var i = 0; i < plan.wods.length; i++) {
      if (String(plan.wods[i].id || '').toLowerCase() === q) return plan.wods[i];
    }
    return null;
  }
  var WOD_FORMAT_LABEL_ = {
    'for_time':        'For Time',
    'amrap':           'AMRAP',
    'emom':            'EMOM',
    'tabata':          'Tabata',
    'rounds_for_time': 'Rounds For Time',
    'chipper':         'Chipper',
  };
  var WOD_SCORE_LABEL_ = {
    'time':         'Time (lower = better)',
    'rounds_reps':  'Rounds + reps',
    'reps':         'Total reps',
    'weight':       'Max weight',
    'load_x_reps':  'Total tonnage',
  };
  function renderWodCard_(plan, session) {
    if (!plan || !session) return '';
    var wodId = String(session['WOD ID'] || '').trim();
    if (!wodId) return '';
    var wod = findWodById_(plan, wodId);
    if (!wod) return '';
    return renderWodCardFromWod_(wod, wodId);
  }
  // Direct WOD-object renderer, used by the Random WOD picker
  // (Phase 4) which already has a WOD in hand and doesn't need an
  // Import-row lookup.
  function renderWodCardFromWod_(wod, fallbackId) {
    if (!wod) return '';
    var wodId = fallbackId || wod.id || '';
    var format = String(wod.format || '').toLowerCase();
    var formatLabel = WOD_FORMAT_LABEL_[format] || (format ? format.toUpperCase() : '');
    var scoreLabel = WOD_SCORE_LABEL_[String(wod.score_type || '').toLowerCase()] || '';
    var meta = [];
    if (wod.time_cap) meta.push(wod.time_cap + ' min cap');
    if (wod.rounds && wod.rounds > 1) meta.push(wod.rounds + ' rounds');
    if (scoreLabel) meta.push(scoreLabel);
    var equipment = String(wod.equipment || '').split(/\s*,\s*/).filter(Boolean);
    var equipmentHtml = equipment.length
      ? '<div class="tmb-merged-wod-equipment">' +
          equipment.map(function (e) {
            return '<span class="tmb-merged-wod-equipment-chip">' + escapeHtml_(e) + '</span>';
          }).join('') +
        '</div>'
      : '';
    return '<div class="tmb-merged-wod-card">' +
      '<div class="tmb-merged-wod-head">' +
        '<span class="tmb-merged-wod-eyebrow">WOD · ' + escapeHtml_(formatLabel || 'Workout') + '</span>' +
        '<h3 class="tmb-merged-wod-name">' + escapeHtml_(wod.canonical_name || wodId) + '</h3>' +
      '</div>' +
      (wod.structure ? '<p class="tmb-merged-wod-structure">' + escapeHtml_(wod.structure) + '</p>' : '') +
      (meta.length ? '<div class="tmb-merged-wod-meta">' + escapeHtml_(meta.join(' · ')) + '</div>' : '') +
      equipmentHtml +
      (wod.notes ? '<p class="tmb-merged-wod-notes">' + escapeHtml_(wod.notes) + '</p>' : '') +
    '</div>';
  }
  // Random WOD picker (#93 Phase 4, merged side). Aggregates WODs
  // across every loaded plan into a single library (deduped by id),
  // then surfaces equipment-filtered random picks. Same localStorage
  // key as the per-plan page (tmb_wod_picker_equipment) so chip
  // state carries over between the two pages.
  var WOD_PICKER_EQUIP_KEY_ = 'tmb_wod_picker_equipment';
  var WOD_PICKER_IGNORE_EQUIP_ = { 'floor': true };
  function normalizeEquipment_(s) {
    var k = String(s || '').replace(/\(.*?\)/g, '').trim().toLowerCase();
    return WOD_PICKER_IGNORE_EQUIP_[k] ? '' : k;
  }
  function aggregateWodsAcrossPlans_() {
    var byId = {};
    Object.keys(_planByToken_).forEach(function (token) {
      var plan = _planByToken_[token];
      if (!plan || !plan.wods) return;
      plan.wods.forEach(function (w) {
        if (!w || !w.id) return;
        if (!byId[w.id]) byId[w.id] = w;
      });
    });
    return Object.keys(byId).map(function (id) { return byId[id]; });
  }
  function collectWodEquipment_(allWods) {
    var seen = {};
    var anyBw = false;
    allWods.forEach(function (wod) {
      var items = String(wod.equipment || '').split(/\s*,\s*/);
      var has = false;
      items.forEach(function (e) {
        var k = normalizeEquipment_(e);
        if (k) { seen[k] = true; has = true; }
      });
      if (!has) anyBw = true;
    });
    if (anyBw) seen['bodyweight'] = true;
    return Object.keys(seen).sort();
  }
  function wodsEligibleFor_(allWods, availableSet) {
    var allChips = collectWodEquipment_(allWods);
    if (allChips.length && allChips.every(function (k) { return !!availableSet[k]; })) {
      return allWods.slice();
    }
    return allWods.filter(function (wod) {
      var needs = String(wod.equipment || '')
        .split(/\s*,\s*/).map(normalizeEquipment_).filter(Boolean);
      if (!needs.length) return !!availableSet['bodyweight'];
      for (var i = 0; i < needs.length; i++) {
        if (!availableSet[needs[i]]) return false;
      }
      return true;
    });
  }
  function readPickerEquipment_() {
    try {
      var raw = localStorage.getItem(WOD_PICKER_EQUIP_KEY_);
      return raw ? JSON.parse(raw) : {};
    } catch (_e) { return {}; }
  }
  function writePickerEquipment_(map) {
    try { localStorage.setItem(WOD_PICKER_EQUIP_KEY_, JSON.stringify(map)); } catch (_e) {}
  }
  function titleCaseWord_(s) {
    return String(s || '').replace(/\w\S*/g, function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });
  }
  function renderRandomWodPicker_() {
    var allWods = aggregateWodsAcrossPlans_();
    if (!allWods.length) return '';
    var equipment = collectWodEquipment_(allWods);
    var checked = readPickerEquipment_();
    var chips = equipment.map(function (e) {
      var on = checked[e] !== false;
      return '<label class="tmb-merged-wod-picker-chip' + (on ? ' is-on' : '') + '">' +
        '<input type="checkbox" data-wod-equip="' + escapeAttr_(e) + '"' + (on ? ' checked' : '') + '>' +
        '<span>' + escapeHtml_(titleCaseWord_(e)) + '</span>' +
      '</label>';
    }).join('');
    var initialEligible = wodsEligibleFor_(allWods, checked).length;
    return '<details class="tmb-merged-wod-picker">' +
      '<summary class="tmb-merged-wod-picker-summary">' +
        '<span class="tmb-merged-wod-picker-title">🎲 Random workout</span>' +
        '<span class="tmb-merged-wod-picker-sub">' + allWods.length + ' in your library</span>' +
      '</summary>' +
      '<div class="tmb-merged-wod-picker-body">' +
        '<p class="tmb-merged-wod-picker-intro">Tick what you have available today, then roll a workout.</p>' +
        '<div class="tmb-merged-wod-picker-chips">' + chips + '</div>' +
        '<div class="tmb-merged-wod-picker-controls">' +
          '<button type="button" class="tmb-merged-wod-picker-roll" data-wod-picker-roll>Roll a workout →</button>' +
          '<span class="tmb-merged-wod-picker-count" data-wod-picker-count>' + initialEligible + ' eligible</span>' +
        '</div>' +
        '<div class="tmb-merged-wod-picker-result" data-wod-picker-result></div>' +
      '</div>' +
    '</details>';
  }

  // Resolve which person the viewer's tick should write to. For solo
  // plans the session's Person column may be blank, fall back to the
  // plan's viewer.name (server fills it from the authenticated email)
  // or settings.athlete (the solo athlete's display name). For group
  // plans where the session has an explicit Person, honor it. (#109 v1)
  function getViewerPerson_(plan, session) {
    var sessPerson = String((session && session.Person) || '').trim();
    if (sessPerson) return sessPerson;
    if (plan && plan.viewer && plan.viewer.name) return String(plan.viewer.name);
    if (plan && plan.settings && plan.settings.athlete) {
      return String(plan.settings.athlete).split(/\s*[&,]\s*/)[0];
    }
    return '';
  }

  // Look up the current Done state for (sessionId, person) in the
  // plan's exports[]. Returns false when no matching row exists.
  function getCurrentDone_(plan, sessionId, person) {
    if (!plan || !plan.exports) return false;
    for (var i = 0; i < plan.exports.length; i++) {
      var ex = plan.exports[i];
      if (String(ex.ID) === String(sessionId) &&
          String(ex.Person || '').trim() === person) {
        return ex.Done === true || String(ex.Done).toUpperCase() === 'TRUE';
      }
    }
    return false;
  }

  // Update local _planByToken_[token].exports[...] in place so the
  // modal's tick state + the merged-plan card visuals reflect the new
  // value immediately (without waiting for the next fetchPlanData_).
  // Mirror of the per-plan setExportField pattern. (#109 v1)
  function updateLocalExport_(token, sessionId, person, fields) {
    var plan = _planByToken_[token];
    if (!plan) return;
    if (!plan.exports) plan.exports = [];
    var ex = null;
    for (var i = 0; i < plan.exports.length; i++) {
      if (String(plan.exports[i].ID) === String(sessionId) &&
          String(plan.exports[i].Person || '').trim() === person) {
        ex = plan.exports[i];
        break;
      }
    }
    if (!ex) {
      ex = { ID: sessionId, Person: person };
      plan.exports.push(ex);
    }
    Object.keys(fields).forEach(function (k) { ex[k] = fields[k]; });
  }

  // Look up the current value of an Export column for (sessionId, person)
  // in the plan's exports[]. Returns '' when no matching row exists or
  // the column is empty. Used by stage-2 fields (notes, time) for their
  // initial state in the modal. (#109 v1 stage 2)
  function getCurrentExportField_(plan, sessionId, person, column) {
    if (!plan || !plan.exports) return '';
    for (var i = 0; i < plan.exports.length; i++) {
      var ex = plan.exports[i];
      if (String(ex.ID) === String(sessionId) &&
          String(ex.Person || '').trim() === person) {
        var v = ex[column];
        return (v == null) ? '' : String(v);
      }
    }
    return '';
  }

  // Generic writeback, used by all the per-field handlers (tick / note
  // / time / sessionField). Each maps to the matching /exec action so
  // the existing server handlers (handleTick_, handleNote_, handleTime_,
  // handleSessionField_) take the write through their normal path.
  // The token in the body routes the write to the right athlete sheet.
  // (#109 v1 stage 2)
  function postMergedWriteback_(action, token, sessionId, person, extra, email, cb) {
    var body = Object.assign({
      action: action,
      token:  token,
      id:     sessionId,
      person: person,
    }, extra || {});
    if (email) body.email = email;
    fetch(endpoint, {
      method:  'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body:    JSON.stringify(body),
    })
      .then(function (r) { return r.json(); })
      .then(function (j) { cb(null, j); })
      .catch(function (err) {
        try { window.tmbLogError && window.tmbLogError('merged_writeback_' + action, String(err && err.message || err), { token: token, sessionId: sessionId, person: person }); } catch (_e) {}
        cb(err, null);
      });
  }

  // POST a tick writeback to the source plan's /exec endpoint. Uses
  // the standard `tick` action so the existing handleTick_ on the
  // server takes the write through its normal path (Done flag,
  // optional PB rollback on untick, cache invalidation, etc.).
  // The token in the body routes the write to the right athlete
  // sheet, that's the trick that makes merged-plan ticks work
  // across multiple source plans from one page. (#109 v1)
  function postTickWriteback_(token, sessionId, person, done, email, cb) {
    postMergedWriteback_('tick', token, sessionId, person,
      { done: done, source: 'planned' }, email, cb);
  }

  // Canvas-resize an image file to a max-dim JPEG data URL. Mirrors
  // the per-plan page's imageFileToResizedDataUrl. Bails cleanly on
  // HEIC/HEIF (iPhone default) with a help message. (#109 v1 stage 3)
  function imageFileToResizedDataUrl_(file, maxDim, quality, cb) {
    var name = String(file && file.name || '');
    var type = String(file && file.type || '');
    if (/heic|heif/i.test(type) || /\.heic$|\.heif$/i.test(name)) {
      cb(new Error('iPhone HEIC photos can\'t be uploaded. Settings → Camera → Formats → "Most Compatible" switches the camera to JPG.'));
      return;
    }
    var reader = new FileReader();
    reader.onload = function (e) {
      var img = new Image();
      img.onload = function () {
        var w = img.width, h = img.height;
        if (w > h && w > maxDim) { h = h * (maxDim / w); w = maxDim; }
        else if (h > maxDim)     { w = w * (maxDim / h); h = maxDim; }
        var canvas = document.createElement('canvas');
        canvas.width = Math.round(w);
        canvas.height = Math.round(h);
        try {
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          cb(null, canvas.toDataURL('image/jpeg', quality));
        } catch (_e) {
          cb(new Error('Couldn\'t process this photo. Try saving it as JPG and re-uploading.'));
        }
      };
      img.onerror = function () {
        cb(new Error('Couldn\'t read this photo. Try a JPG or PNG.'));
      };
      img.src = e.target.result;
    };
    reader.onerror = function () { cb(new Error('Couldn\'t read this file.')); };
    reader.readAsDataURL(file);
  }

  // Build the /exec photo-proxy URL for a Drive fileId. Server resolves
  // the bytes from the athlete's Drive folder via the token and returns
  // them as a base64 data URL. Matches the per-plan page's photo flow.
  function photoProxyUrl_(token, fileId) {
    return endpoint + '?token=' + encodeURIComponent(token) + '&photo=' + encodeURIComponent(fileId);
  }

  // Comma-separated file IDs from PLAN.exports[].Images for (id, person).
  function getSessionImageIds_(plan, sessionId, person) {
    return getSessionFileIds_(plan, sessionId, person, 'Images');
  }
  // Same shape for Videos, different column on the export row.
  function getSessionVideoIds_(plan, sessionId, person) {
    return getSessionFileIds_(plan, sessionId, person, 'Videos');
  }
  function getSessionFileIds_(plan, sessionId, person, col) {
    if (!plan || !plan.exports) return [];
    for (var i = 0; i < plan.exports.length; i++) {
      var ex = plan.exports[i];
      if (String(ex.ID) === String(sessionId) &&
          String(ex.Person || '').trim() === person) {
        var raw = String(ex[col] || '').trim();
        if (!raw) return [];
        return raw.split(/\s*,\s*/).filter(Boolean);
      }
    }
    return [];
  }

  // Append a new fileId to PLAN.exports[].Images for (id, person). The
  // local optimistic mirror of appendToExportField_ on the server.
  function appendLocalImageId_(token, sessionId, person, fileId) {
    var plan = _planByToken_[token];
    if (!plan) return;
    if (!plan.exports) plan.exports = [];
    var ex = null;
    for (var i = 0; i < plan.exports.length; i++) {
      if (String(plan.exports[i].ID) === String(sessionId) &&
          String(plan.exports[i].Person || '').trim() === person) {
        ex = plan.exports[i];
        break;
      }
    }
    if (!ex) {
      ex = { ID: sessionId, Person: person };
      plan.exports.push(ex);
    }
    var existing = String(ex.Images || '').trim();
    ex.Images = existing ? (existing + ',' + fileId) : fileId;
  }
  // Mirror appendLocalImageId_ for videos.
  function appendLocalVideoId_(token, sessionId, person, fileId) {
    var plan = _planByToken_[token];
    if (!plan) return;
    if (!plan.exports) plan.exports = [];
    var ex = null;
    for (var i = 0; i < plan.exports.length; i++) {
      if (String(plan.exports[i].ID) === String(sessionId) &&
          String(plan.exports[i].Person || '').trim() === person) {
        ex = plan.exports[i];
        break;
      }
    }
    if (!ex) {
      ex = { ID: sessionId, Person: person };
      plan.exports.push(ex);
    }
    var existing = String(ex.Videos || '').trim();
    ex.Videos = existing ? (existing + ',' + fileId) : fileId;
  }

  // Build a movementId → canonical_name map for a plan. Used to render
  // human-readable movement names for programmed sets. (#109 v1 stage 4a)
  function movementsByIdMap_(plan) {
    var out = {};
    if (!plan || !plan.movements) return out;
    plan.movements.forEach(function (m) {
      if (m && m.id) out[String(m.id)] = String(m.canonical_name || m.id);
    });
    return out;
  }

  // Programmed sets for a session, sorted by Order. Each row carries
  // target_reps / target_weight_kg / target_distance / target_duration_s /
  // target_pace / target_power_w / target_rpe / notes / phase.
  function getProgrammedSetsFor_(plan, sessionId) {
    if (!plan || !plan.programmedSets) return [];
    return plan.programmedSets
      .filter(function (r) { return String(r.session_id) === String(sessionId); })
      .slice()
      .sort(function (a, b) { return (a.order || 0) - (b.order || 0); });
  }

  // Logged sets for a (session, person), sorted by Set number.
  function getLoggedSetsFor_(plan, sessionId, person) {
    if (!plan || !plan.sets) return [];
    return plan.sets
      .filter(function (r) {
        return String(r.ID) === String(sessionId) &&
               String(r.Person || '').trim() === person;
      })
      .slice()
      .sort(function (a, b) { return Number(a.Set || 0) - Number(b.Set || 0); });
  }

  // Format a single programmed-set row as one human-readable line.
  // Picks the most descriptive fields based on metric_type, defaulting
  // to sets × reps @ weight when in doubt.
  function formatProgrammedRow_(row, mvMap) {
    var name = mvMap[String(row.movement_id)] || String(row.movement_id || '');
    var bits = [];
    if (row.sets_or_repeats) bits.push(row.sets_or_repeats + '×');
    if (row.target_reps != null && row.target_reps !== '') bits.push(row.target_reps + ' reps');
    if (row.target_weight_kg != null && row.target_weight_kg !== '') bits.push('@ ' + row.target_weight_kg + 'kg');
    if (row.target_distance != null && row.target_distance !== '') bits.push(row.target_distance + ' dist');
    if (row.target_duration_s != null && row.target_duration_s !== '') bits.push(row.target_duration_s + 's');
    if (row.target_pace) bits.push(row.target_pace + ' pace');
    if (row.target_power_w != null && row.target_power_w !== '') bits.push(row.target_power_w + 'W');
    if (row.target_rpe != null && row.target_rpe !== '') bits.push('@ RPE ' + row.target_rpe);
    var meta = bits.join(' ');
    var notes = String(row.notes || '').trim();
    var phase = String(row.phase || '').toLowerCase();
    var phaseTag = (phase && phase !== 'main') ? '<span class="tmb-merged-set-phase">' + escapeHtml_(phase) + '</span>' : '';
    return '<div class="tmb-merged-set-row">' +
      phaseTag +
      '<span class="tmb-merged-set-name">' + escapeHtml_(name) + '</span>' +
      (meta ? '<span class="tmb-merged-set-meta">' + escapeHtml_(meta) + '</span>' : '') +
      (notes ? '<span class="tmb-merged-set-notes">' + escapeHtml_(notes) + '</span>' : '') +
    '</div>';
  }

  // Format a logged-set row. Reads Family + Sets + Reps + Weight + RPE
  // + Notes + Completed.
  function formatLoggedRow_(row) {
    var n = Number(row.Set || 0);
    var fam = String(row.Family || '').trim();
    var done = row.Completed === true || String(row.Completed).toUpperCase() === 'TRUE';
    var bits = [];
    if (row.Sets != null && row.Sets !== '')   bits.push(row.Sets + '×');
    if (row.Reps != null && row.Reps !== '')   bits.push(row.Reps + ' reps');
    if (row.Weight != null && row.Weight !== '') bits.push('@ ' + row.Weight + 'kg');
    if (row.Time != null && row.Time !== '')   bits.push(row.Time + ' min');
    if (row.Distance != null && row.Distance !== '') bits.push(row.Distance + ' dist');
    if (row.Pace)                              bits.push(row.Pace + ' pace');
    if (row.Calories != null && row.Calories !== '') bits.push(row.Calories + ' kcal');
    if (row.RPE != null && row.RPE !== '')     bits.push('@ RPE ' + row.RPE);
    var meta = bits.join(' ');
    var notes = String(row.Notes || '').trim();
    return '<div class="tmb-merged-set-row' + (done ? ' is-done' : '') + '">' +
      '<span class="tmb-merged-set-num">#' + n + '</span>' +
      '<span class="tmb-merged-set-name">' + escapeHtml_(fam || '(no movement)') + '</span>' +
      (meta ? '<span class="tmb-merged-set-meta">' + escapeHtml_(meta) + '</span>' : '') +
      (notes ? '<span class="tmb-merged-set-notes">' + escapeHtml_(notes) + '</span>' : '') +
    '</div>';
  }

  // Which sets-editor columns apply to a movement's metric_type. Mirrors
  // METRIC_TYPE_FIELDS_ in training-plan-v2.js so the merged modal greys
  // the same cells the per-plan modal would. (audit gap #1)
  var METRIC_TYPE_FIELDS_ = {
    'reps_weight':    { reps: 1, weight: 1 },
    'reps':           { reps: 1 },
    'duration':       { time: 1 },
    'distance_pace':  { distance: 1, time: 1, pace: 1 },
    'duration_pace':  { time: 1, pace: 1 },
    'time_power':     { time: 1 },
    'rounds_score':   { time: 1 },
    'time_calories':  { time: 1, calories: 1 },
    'calories':       { calories: 1 },
  };
  var METRIC_TYPE_ALWAYS_FIELDS_ = { family: 1, sets: 1, rpe: 1, completed: 1 };
  function fieldAppliesToMetricType_(field, metricType) {
    if (METRIC_TYPE_ALWAYS_FIELDS_[field]) return true;
    if (!metricType) return true;  // unknown movement → show everything (legacy)
    var map = METRIC_TYPE_FIELDS_[metricType];
    if (!map) return true;
    return !!map[field];
  }
  // Find a canonical movement entry by name (canonical_name or alias,
  // case-insensitive). Returns the movement object or null.
  function findMovementByName_(plan, name) {
    var q = String(name || '').trim().toLowerCase();
    if (!q || !plan || !plan.movements) return null;
    for (var i = 0; i < plan.movements.length; i++) {
      var m = plan.movements[i];
      if (!m) continue;
      if (String(m.canonical_name || '').trim().toLowerCase() === q) return m;
      var aliases = String(m.aliases || '').split(/\s*,\s*/).filter(Boolean);
      for (var j = 0; j < aliases.length; j++) {
        if (aliases[j].toLowerCase() === q) return m;
      }
    }
    return null;
  }

  // Merge programmed + logged sets into a unified editable list. Each
  // entry has: setNumber, family, sets, reps, weight, time, rpe,
  // completed, isProgrammedOnly (placeholder values, no logged row yet).
  // (#109 v1 stage 4b)
  function buildEditableSets_(programmed, logged, mvMap) {
    var byNum = {};
    // Logged first, these are authoritative.
    logged.forEach(function (r) {
      var n = Number(r.Set || 0);
      if (!n) return;
      byNum[n] = {
        setNumber: n,
        family:    String(r.Family || ''),
        sets:      r.Sets,
        reps:      r.Reps,
        weight:    r.Weight,
        distance:  r.Distance,
        time:      r.Time,
        pace:      r.Pace,
        calories:  r.Calories,
        rpe:       r.RPE,
        completed: r.Completed === true || String(r.Completed).toUpperCase() === 'TRUE',
        isProgrammedOnly: false,
      };
    });
    // Programmed second, fills gaps (one row per prescription entry,
    // numbered from 1; uses target_* values as the "expected" inputs).
    programmed.forEach(function (r, i) {
      var n = i + 1;
      if (byNum[n]) return;  // logged row already at this slot
      byNum[n] = {
        setNumber: n,
        family:    mvMap[String(r.movement_id)] || String(r.movement_id || ''),
        sets:      r.sets_or_repeats != null ? r.sets_or_repeats : '',
        reps:      r.target_reps != null ? r.target_reps : '',
        weight:    r.target_weight_kg != null ? r.target_weight_kg : '',
        distance:  r.target_distance != null ? r.target_distance : '',
        time:      r.target_duration_s != null ? secondsToMmSs_(r.target_duration_s) : '',
        pace:      r.target_pace || '',
        calories:  r.target_calories != null ? r.target_calories : '',
        rpe:       r.target_rpe != null ? r.target_rpe : '',
        completed: false,
        isProgrammedOnly: true,
      };
    });
    var out = [];
    Object.keys(byNum)
      .map(Number)
      .sort(function (a, b) { return a - b; })
      .forEach(function (k) { out.push(byNum[k]); });
    return out;
  }

  // Pace triad, auto-derive the third of Distance / Time / Pace when
  // the other two are present. Ported from training-plan-v2.js so the
  // merged modal's running/cardio rows behave the same way. Time is
  // input as MM:SS in the editor; we normalise via parseTimeSec_.
  function parsePaceSec_(str) {
    var s = String(str || '').trim();
    if (!s) return NaN;
    if (s.indexOf(':') >= 0) {
      var p = s.split(':');
      var m = Number(p[0]);
      var sec = Number(p[1] || 0);
      if (!isFinite(m) || !isFinite(sec)) return NaN;
      return m * 60 + sec;
    }
    var n = Number(s);
    if (!isFinite(n)) return NaN;
    return n > 30 ? n : n * 60;
  }
  function formatPace_(secs) {
    if (!isFinite(secs) || secs <= 0) return '';
    var m = Math.floor(secs / 60);
    var s = Math.round(secs - m * 60);
    if (s === 60) { m += 1; s = 0; }
    return m + ':' + (s < 10 ? '0' + s : s);
  }
  // Time MM:SS / HH:MM:SS / raw seconds → seconds.
  function parseTimeSec_(str) {
    var s = String(str || '').trim();
    if (!s) return NaN;
    if (s.indexOf(':') >= 0) {
      var parts = s.split(':').map(Number);
      if (parts.some(function (n) { return !isFinite(n); })) return NaN;
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      if (parts.length === 2) return parts[0] * 60 + parts[1];
    }
    var n = Number(s);
    return isFinite(n) ? n : NaN;
  }
  function readPaceTriad_(rowEl) {
    function val(field) {
      var el = rowEl.querySelector('[data-set-field="' + field + '"]');
      return el ? String(el.value || '').trim() : '';
    }
    var distance = val('distance');
    var time     = val('time');
    var pace     = val('pace');
    return {
      distance: distance,
      time:     time,
      pace:     pace,
      distanceN: distance === '' ? NaN : Number(distance),
      timeSec:   parseTimeSec_(time),
      paceSec:   parsePaceSec_(pace),
    };
  }
  // Pure derivation, returns { field, value } or null. Callers stage
  // the value through the standard stageFieldChange_ path so it picks
  // up the same dirty-tracking + Save-button update as a real edit.
  function deriveTriadFromInputs_(rowEl, changedField) {
    var t = readPaceTriad_(rowEl);
    var hasDist = isFinite(t.distanceN) && t.distanceN > 0;
    var hasTime = isFinite(t.timeSec) && t.timeSec > 0;
    var hasPace = isFinite(t.paceSec) && t.paceSec > 0;
    var filled = (hasDist ? 1 : 0) + (hasTime ? 1 : 0) + (hasPace ? 1 : 0);
    if (filled < 2) return null;
    if (filled === 2) {
      if (!hasDist && changedField !== 'distance') {
        var d = t.timeSec / t.paceSec;
        return { field: 'distance', value: (Math.round(d * 100) / 100).toString() };
      }
      if (!hasTime && changedField !== 'time') {
        var ts = t.distanceN * t.paceSec;
        // Format back to MM:SS (or HH:MM:SS for very long times).
        return { field: 'time', value: formatSecondsTime_(Math.round(ts)) };
      }
      if (!hasPace && changedField !== 'pace') {
        var ps = t.timeSec / t.distanceN;
        return { field: 'pace', value: formatPace_(ps) };
      }
      return null;
    }
    // All three present, recompute the natural downstream.
    if (changedField === 'distance' || changedField === 'time') {
      return { field: 'pace', value: formatPace_(t.timeSec / t.distanceN) };
    }
    if (changedField === 'pace') {
      return { field: 'time', value: formatSecondsTime_(Math.round(t.distanceN * t.paceSec)) };
    }
    return null;
  }
  function formatSecondsTime_(secs) {
    if (!isFinite(secs) || secs <= 0) return '';
    var h = Math.floor(secs / 3600);
    var rest = secs - h * 3600;
    var m = Math.floor(rest / 60);
    var s = Math.round(rest - m * 60);
    if (s === 60) { m += 1; s = 0; }
    if (m === 60) { h += 1; m = 0; }
    var mm = (m < 10 ? '0' : '') + m;
    var ss = (s < 10 ? '0' : '') + s;
    if (h > 0) return h + ':' + mm + ':' + ss;
    return m + ':' + ss;
  }

  function secondsToMmSs_(secs) {
    var s = Number(secs);
    if (!isFinite(s) || s <= 0) return '';
    var m = Math.floor(s / 60);
    var r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  // One editable row. data-set-num holds the Set number; per-input
  // data-field tags the column for the blur-save dispatcher. Plan ref
  // is needed so we can resolve the row's movement and mask cells the
  // metric_type doesn't use (e.g. WEIGHT on a bodyweight row).
  function buildEditableSetRow_(r, plan) {
    function val(v) { return v == null ? '' : String(v); }
    var rowCls = 'tmb-merged-editor-row' +
      (r.isProgrammedOnly ? ' is-programmed' : '') +
      (r.completed ? ' is-done' : '');
    var movement = findMovementByName_(plan, r.family);
    var metricType = movement ? String(movement.metric_type || '') : '';
    // Append `--na` to inputs whose column doesn't apply for this row's
    // metric_type. CSS dims + disables them so the row visually reads
    // as "these fields aren't relevant here."
    function cls_(field) {
      var base = 'tmb-merged-editor-input';
      if (field === 'family') base += ' tmb-merged-editor-input--movement';
      if (!fieldAppliesToMetricType_(field, metricType)) base += ' tmb-merged-editor-input--na';
      return base;
    }
    return '<div class="' + rowCls + '" data-set-num="' + r.setNumber + '">' +
      '<button type="button" class="tmb-merged-editor-x" data-set-delete="' + r.setNumber + '" aria-label="Delete set">×</button>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--movement">' +
        '<input type="text" class="' + cls_('family') + '" data-set-field="family" value="' + escapeAttr_(val(r.family)) + '" placeholder="Movement" list="tmb-merged-movements" autocomplete="off">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--sets">' +
        '<input type="number" inputmode="numeric" min="0" step="1" class="' + cls_('sets') + '" data-set-field="sets" value="' + escapeAttr_(val(r.sets)) + '">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--reps">' +
        '<input type="number" inputmode="numeric" min="0" step="1" class="' + cls_('reps') + '" data-set-field="reps" value="' + escapeAttr_(val(r.reps)) + '">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--rpe">' +
        '<input type="number" inputmode="numeric" min="0" max="10" step="1" class="' + cls_('rpe') + '" data-set-field="rpe" value="' + escapeAttr_(val(r.rpe)) + '" placeholder="-">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--weight">' +
        '<input type="number" inputmode="decimal" min="0" step="0.5" class="' + cls_('weight') + '" data-set-field="weight" value="' + escapeAttr_(val(r.weight)) + '">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--distance">' +
        '<input type="number" inputmode="decimal" min="0" step="0.01" class="' + cls_('distance') + '" data-set-field="distance" value="' + escapeAttr_(val(r.distance)) + '" placeholder="-">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--time">' +
        '<input type="text" inputmode="text" class="' + cls_('time') + '" data-set-field="time" value="' + escapeAttr_(val(r.time)) + '" placeholder="-">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--pace">' +
        '<input type="text" inputmode="text" class="' + cls_('pace') + '" data-set-field="pace" value="' + escapeAttr_(val(r.pace)) + '" placeholder="-">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--calories">' +
        '<input type="number" inputmode="numeric" min="0" step="1" class="' + cls_('calories') + '" data-set-field="calories" value="' + escapeAttr_(val(r.calories)) + '" placeholder="-">' +
      '</div>' +
      '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--done">' +
        '<label class="tmb-merged-editor-done">' +
          '<input type="checkbox" data-set-field="completed"' + (r.completed ? ' checked' : '') + '>' +
          '<span class="tmb-merged-editor-done-mark" aria-hidden="true">✓</span>' +
        '</label>' +
      '</div>' +
    '</div>';
  }
  // "Last time" lookup. Finds the same workout in an earlier week
   // (matched on Name + Day + Slot) and returns the logged sets that
   // produced it. Mirrors findPreviousSession in training-plan-v2.js
   // but scoped to a single source plan. (audit gap #2)
  var DAY_LABELS_ = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };
  function findPreviousSession_(plan, currentRow, person) {
    if (!plan || !currentRow || !person) return null;
    var pNorm = String(person).trim();
    var currentId   = String(currentRow.ID   || '');
    var currentDay  = String(currentRow.Day  || '').toLowerCase();
    var currentSlot = String(currentRow.Slot || '').trim();
    var currentWeekN = parseInt(String(currentRow.Week || '').replace(/^w/i, ''), 10);
    var currentName = String(currentRow.Name || '').trim();
    if (!currentName || !currentDay) return null;
    function nameKey_(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
    var currentNameKey = nameKey_(currentName);
    var setsById = {};
    (plan.sets || []).forEach(function (s) {
      if (String(s.Person || '').trim() !== pNorm) return;
      var id = String(s.ID || '');
      (setsById[id] || (setsById[id] = [])).push(s);
    });
    var candidates = [];
    (plan.imports || []).forEach(function (r) {
      if (String(r.ID) === currentId) return;
      if (String(r.Day  || '').toLowerCase() !== currentDay)  return;
      if (String(r.Slot || '').trim()         !== currentSlot) return;
      if (nameKey_(r.Name) !== currentNameKey) return;
      var weekN = parseInt(String(r.Week || '').replace(/^w/i, ''), 10);
      if (!isFinite(weekN) || (isFinite(currentWeekN) && weekN >= currentWeekN)) return;
      var rSets = setsById[String(r.ID)] || [];
      if (!rSets.length) return;
      candidates.push({ row: r, weekN: weekN, sets: rSets });
    });
    if (!candidates.length) return null;
    candidates.sort(function (a, b) { return b.weekN - a.weekN; });
    return candidates[0];
  }
  function titleCase_(s) {
    return String(s || '').replace(/\w\S*/g, function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });
  }
  function renderPreviousSetsSummary_(plan, currentRow, person) {
    var prev = findPreviousSession_(plan, currentRow, person);
    if (!prev || !prev.sets.length) return '';
    var byFamily = {};
    prev.sets.forEach(function (s) {
      var f = String(s.Family || '').trim();
      if (!f) return;
      (byFamily[f] || (byFamily[f] = [])).push(s);
    });
    var keys = Object.keys(byFamily);
    if (!keys.length) return '';
    var weekLabel = 'Wk ' + prev.weekN;
    var dayLabel = DAY_LABELS_[String(prev.row.Day || '').toLowerCase()] || prev.row.Day || '';
    var html = ['<div class="tmb-merged-prev-sets">'];
    html.push('<div class="tmb-merged-prev-sets-head">Last time · ' +
      escapeHtml_(weekLabel + (dayLabel ? ' ' + dayLabel : '')) + '</div>');
    keys.forEach(function (family) {
      var entries = byFamily[family].sort(function (a, b) {
        return Number(a.Set || 0) - Number(b.Set || 0);
      });
      var bits = entries.map(function (s) {
        var w = (s.Weight === '' || s.Weight == null) ? '' : s.Weight + 'kg';
        var r = (s.Reps   === '' || s.Reps   == null) ? '' : s.Reps;
        var mult = (s.Sets === '' || s.Sets == null) ? '' : String(s.Sets);
        if (mult && Number(mult) > 1) {
          var rhs = (w && r) ? (r + ' × ' + w) : (w || (r ? r + ' rep' + (Number(r) === 1 ? '' : 's') : ''));
          return rhs ? mult + ' × ' + rhs : mult + ' sets';
        }
        if (w && r) return w + ' × ' + r;
        return w || (r ? r + ' rep' + (Number(r) === 1 ? '' : 's') : '');
      }).filter(Boolean);
      if (!bits.length) return;
      html.push('<div class="tmb-merged-prev-sets-row">' +
        '<span class="tmb-merged-prev-sets-family">' + escapeHtml_(titleCase_(family)) + '</span>' +
        '<span class="tmb-merged-prev-sets-values">' + escapeHtml_(bits.join(', ')) + '</span>' +
      '</div>');
    });
    html.push('</div>');
    return html.join('');
  }

  // Re-apply metric_type masks on one row in-place. Called after the
  // user types a new movement into the Family input so columns adapt
  // without a full re-render. (matches refreshRowMetricMask_ in v2)
  // Also flips a `tmb-merged-editor-row--unknown` class when the typed
  // value doesn't match any canonical name / alias, drives a small
  // "Unrecognised movement" hint below the row.
  function refreshRowMetricMask_(rowEl, plan) {
    if (!rowEl) return;
    var famInput = rowEl.querySelector('[data-set-field="family"]');
    var typed = famInput ? String(famInput.value || '').trim() : '';
    var movement = findMovementByName_(plan, typed);
    var metricType = movement ? String(movement.metric_type || '') : '';
    rowEl.querySelectorAll('[data-set-field]').forEach(function (el) {
      var field = el.getAttribute('data-set-field') || '';
      if (!field || el.type === 'checkbox') return;
      var applies = fieldAppliesToMetricType_(field, metricType);
      el.classList.toggle('tmb-merged-editor-input--na', !applies);
    });
    // Unrecognised flag, only when something was typed but no match.
    rowEl.classList.toggle('tmb-merged-editor-row--unknown', !!typed && !movement);
  }

  // Update local PLAN.sets to mirror a server upsert. Allocates a new
  // row if none exists at (id, person, setNumber). (#109 v1 stage 4b)
  function updateLocalSet_(token, sessionId, person, setNumber, fields) {
    var plan = _planByToken_[token];
    if (!plan) return;
    if (!plan.sets) plan.sets = [];
    var row = null;
    for (var i = 0; i < plan.sets.length; i++) {
      if (String(plan.sets[i].ID) === String(sessionId) &&
          String(plan.sets[i].Person || '').trim() === person &&
          Number(plan.sets[i].Set || 0) === Number(setNumber)) {
        row = plan.sets[i];
        break;
      }
    }
    if (!row) {
      row = { ID: sessionId, Person: person, Set: setNumber };
      plan.sets.push(row);
    }
    Object.keys(fields).forEach(function (k) { row[k] = fields[k]; });
  }

  // Remove a set row from local PLAN.sets after server delete.
  function removeLocalSet_(token, sessionId, person, setNumber) {
    var plan = _planByToken_[token];
    if (!plan || !plan.sets) return;
    plan.sets = plan.sets.filter(function (r) {
      return !(String(r.ID) === String(sessionId) &&
               String(r.Person || '').trim() === person &&
               Number(r.Set || 0) === Number(setNumber));
    });
  }

  // Compute the next Set number for a new row given currently-displayed rows.
  function nextSetNumber_(tableEl) {
    var rows = tableEl.querySelectorAll('.tmb-merged-editor-row[data-set-num]');
    var max = 0;
    rows.forEach(function (r) {
      var n = Number(r.getAttribute('data-set-num') || 0);
      if (n > max) max = n;
    });
    return max + 1;
  }

  function openSessionModal_(entry, email) {
    if (!entry || !entry.session) return;
    var s = entry.session;
    var title = String(s.Name || s.Title || s.Category || 'Session');
    var slot = String(s.Slot || 'All day');
    var meta = [];
    if (s.Category) meta.push(String(s.Category));
    if (s['Planned (min)']) meta.push(s['Planned (min)'] + ' min');
    if (s.Priority) meta.push(String(s.Priority));
    var sep = entry.pageUrl && entry.pageUrl.indexOf('?') === -1 ? '?' : '&';
    var href = entry.pageUrl ? entry.pageUrl + sep + 'email=' + encodeURIComponent(email || '') : '';
    var descHtml = renderDescription_(s.Description);

    // Per-source plan data, used by all the interactive sub-sections
    // below (tick / time / notes / photos / sets). #109 v1
    var plan = _planByToken_[entry.sourceToken] || null;
    // WOD prescription card, shown when the Import row references a
    // canonical WOD by id. The library entry carries the structure,
    // time cap, score type and equipment so the modal doesn't have
    // to rely on the Description for the prescription. (#93 Phase 3)
    var wodCardHtml = renderWodCard_(plan, s);
    var tickPerson = getViewerPerson_(plan, s);
    var tickDone = tickPerson ? getCurrentDone_(plan, s.ID, tickPerson) : false;
    var currentNote = tickPerson ? getCurrentExportField_(plan, s.ID, tickPerson, 'Notes') : '';
    var currentTime = tickPerson ? getCurrentExportField_(plan, s.ID, tickPerson, 'Actual (min)') : '';
    var photoIds = tickPerson ? getSessionImageIds_(plan, s.ID, tickPerson) : [];
    var videoIds = tickPerson ? getSessionVideoIds_(plan, s.ID, tickPerson) : [];
    var plannedMin = Number(s['Planned (min)'] || 0);

    // SETS, editable table mirroring the per-plan modal's sets editor.
    // Columns: × | Movement | Sets | Reps | RPE | Weight | Time | ✓.
    // Pre-fills from PLAN.programmedSets where no logged row exists for
    // that order; otherwise renders the logged row. Per-input blur-save
    // routes through postMergedWriteback_('set', ...). (#109 v1 stage 4b)
    var setsHtml = '';
    if (tickPerson) {
      var mvMap = movementsByIdMap_(plan);
      var programmedRows = getProgrammedSetsFor_(plan, s.ID);
      var loggedRows = getLoggedSetsFor_(plan, s.ID, tickPerson);
      // Build a unified "displayed sets" list: one row per programmed
      // entry (with target values as the placeholders), overlaid by
      // any matching logged row at the same Set number. Extra logged
      // rows beyond programmed get appended below.
      var displayedSets = buildEditableSets_(programmedRows, loggedRows, mvMap);
      var rowsHtml = displayedSets.map(function (r) {
        return buildEditableSetRow_(r, plan);
      }).join('');
      // Movement autocomplete, datalist of canonical names + aliases
      // from the source plan. The browser surfaces matching options
      // as the user types in any row's Movement input (list="…" attr).
      var mvNames = {};
      ((plan && plan.movements) || []).forEach(function (m) {
        if (!m) return;
        var canon = String(m.canonical_name || '').trim();
        if (canon) mvNames[canon] = 1;
        String(m.aliases || '').split(/\s*,\s*/).forEach(function (a) {
          var t = String(a || '').trim();
          if (t) mvNames[t] = 1;
        });
      });
      var datalistHtml = '<datalist id="tmb-merged-movements">' +
        Object.keys(mvNames).sort().map(function (n) {
          return '<option value="' + escapeAttr_(n) + '">';
        }).join('') +
      '</datalist>';
      var prevSetsHtml = renderPreviousSetsSummary_(plan, s, tickPerson);
      setsHtml =
        prevSetsHtml +
        '<div class="tmb-merged-editor" data-sets-editor>' +
          datalistHtml +
          '<div class="tmb-merged-editor-head">SETS</div>' +
          '<div class="tmb-merged-editor-table" data-sets-table>' +
            '<div class="tmb-merged-editor-headrow">' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--x"></div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--movement">MOVEMENT</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--sets">SETS</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--reps">REPS</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--rpe">RPE</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--weight">WEIGHT (KG)</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--distance">DIST</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--time">TIME</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--pace">PACE</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--calories">CAL</div>' +
              '<div class="tmb-merged-editor-cell tmb-merged-editor-cell--done">#</div>' +
            '</div>' +
            rowsHtml +
          '</div>' +
          '<button type="button" class="tmb-merged-editor-add" data-set-add>+ ADD SET</button>' +
        '</div>';
    }

    // SESSION TOTALS. Time input + Mark completed pill, side-by-side
    // (matches the per-plan modal's "Session totals" block). (#109 v1)
    var sessionTotalsHtml = '';
    if (tickPerson) {
      sessionTotalsHtml =
        '<div class="tmb-merged-totals">' +
          '<div class="tmb-merged-totals-head">Session totals</div>' +
          '<div class="tmb-merged-totals-row">' +
            '<label class="tmb-merged-totals-time">' +
              '<span class="tmb-merged-log-label">Time</span>' +
              '<div class="tmb-merged-totals-time-input">' +
                '<input type="number" inputmode="numeric" pattern="[0-9]*" min="0" step="1" data-time-input class="tmb-merged-log-input" placeholder="-" value="' + escapeAttr_(currentTime) + '">' +
                '<span class="tmb-merged-totals-unit">min</span>' +
              '</div>' +
              (plannedMin > 0 ? '<span class="tmb-merged-totals-planned">Planned: ' + plannedMin + ' min</span>' : '') +
              '<span class="tmb-merged-log-status" data-time-status></span>' +
            '</label>' +
            '<label class="tmb-merged-totals-done">' +
              '<span class="tmb-merged-totals-done-label">Mark completed</span>' +
              '<input type="checkbox" data-tick-input ' + (tickDone ? 'checked' : '') + '>' +
              '<span class="tmb-merged-tick-mark" aria-hidden="true">✓</span>' +
              '<span class="tmb-merged-tick-status" data-tick-status></span>' +
            '</label>' +
          '</div>' +
        '</div>';
    }

    // NOTES, single textarea below the totals row, mirroring the
    // per-plan modal's notes affordance. (#109 v1)
    var notesHtml = '';
    if (tickPerson) {
      notesHtml =
        '<div class="tmb-merged-notes">' +
          '<textarea data-note-input class="tmb-merged-log-textarea" rows="3" placeholder="Notes, how did it go?">' + escapeHtml_(currentNote) + '</textarea>' +
          '<span class="tmb-merged-log-status" data-note-status></span>' +
        '</div>';
    }

    // PHOTOS, chip + count + Add Photo affordance + thumb strip below.
    // (#109 v1 stage 3, order moved per layout fix.)
    var photoHtml = '';
    if (tickPerson) {
      var thumbsHtml = photoIds.map(function (fid) {
        var url = photoProxyUrl_(entry.sourceToken, fid);
        return '<button type="button" class="tmb-merged-photo-thumb" data-photo-open="' + escapeAttr_(url) + '" data-fid="' + escapeAttr_(fid) + '" aria-label="Open photo">' +
          '<img src="' + escapeAttr_(url) + '" loading="lazy" alt="">' +
        '</button>';
      }).join('');
      photoHtml =
        '<div class="tmb-merged-photos" data-photos-row>' +
          '<div class="tmb-merged-photos-head">' +
            '<span class="tmb-merged-log-label">Photos <span data-photos-count>' + photoIds.length + '</span></span>' +
            '<span class="tmb-merged-photos-status" data-photos-status></span>' +
          '</div>' +
          '<div class="tmb-merged-photos-strip" data-photos-strip>' +
            thumbsHtml +
            '<label class="tmb-merged-photo-add" tabindex="0">' +
              '<input type="file" accept="image/*" capture="environment" data-photo-input hidden>' +
              '<span aria-hidden="true">+</span>' +
              '<span class="tmb-merged-photo-add-label">Add photo</span>' +
            '</label>' +
          '</div>' +
        '</div>';
    }

    // VIDEOS, mirror photos. Single-shot dataURL POST capped at 7 MB
    // (base64 inflates ~33%, doPost ceiling 10 MB). Larger files refused
    // with a help message pointing at the per-plan modal's chunked
    // uploader. Click thumb → in-modal lightbox plays the video inline.
    var videoHtml = '';
    if (tickPerson) {
      var videoThumbsHtml = videoIds.map(function (fid) {
        var url = photoProxyUrl_(entry.sourceToken, fid);
        return '<button type="button" class="tmb-merged-photo-thumb tmb-merged-video-thumb" data-video-open="' + escapeAttr_(url) + '" data-fid="' + escapeAttr_(fid) + '" aria-label="Play video">' +
          '<video src="' + escapeAttr_(url) + '" preload="metadata" muted playsinline></video>' +
          '<span class="tmb-merged-video-thumb-icon" aria-hidden="true">▶</span>' +
        '</button>';
      }).join('');
      videoHtml =
        '<div class="tmb-merged-photos tmb-merged-videos" data-videos-row>' +
          '<div class="tmb-merged-photos-head">' +
            '<span class="tmb-merged-log-label">Videos <span data-videos-count>' + videoIds.length + '</span></span>' +
            '<span class="tmb-merged-photos-status" data-videos-status></span>' +
          '</div>' +
          '<div class="tmb-merged-photos-strip" data-videos-strip>' +
            videoThumbsHtml +
            '<label class="tmb-merged-photo-add" tabindex="0">' +
              '<input type="file" accept="video/*" data-video-input hidden>' +
              '<span aria-hidden="true">+</span>' +
              '<span class="tmb-merged-photo-add-label">Add video</span>' +
            '</label>' +
          '</div>' +
        '</div>';
    }

    var existing = document.getElementById('tmb-merged-modal');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.id = 'tmb-merged-modal';
    overlay.className = 'tmb-merged-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    // Session state for the batch-save model. Mirrors per-plan v3.
    //   pendingSets[setNumber] = { fields: {...}, localFields: {...} } where
    //   fields use server-side keys and localFields use PLAN.sets column
    //   names. The map collapses repeated edits to the same cell so we
    //   only ever POST the final value per set on save.
    overlay._tmbSession = {
      dirty: false,
      saving: false,
      lastError: null,
      pendingSets: {},
    };
    // Hard-set position + size inline so a CSS load order race or a
    // sibling transform animation can't briefly resolve the containing
    // block to anything other than the viewport. inset:0 in CSS does
    // the same thing, this is belt-and-braces.
    overlay.style.cssText =
      'position: fixed;' +
      'top: 0; left: 0; right: 0; bottom: 0;' +
      'width: 100vw; height: 100vh;' +
      'z-index: 10000;';
    overlay.innerHTML =
      '<div class="tmb-merged-modal" style="--plan-color: ' + entry.color + '">' +
        '<button type="button" class="tmb-merged-modal-close" aria-label="Close">&times;</button>' +
        '<div class="tmb-merged-modal-header">' +
          '<span class="tmb-merged-modal-plan">' + escapeHtml_(entry.planTitle || 'Plan') + '</span>' +
          '<h2 class="tmb-merged-modal-title">' + escapeHtml_(title) + '</h2>' +
          '<span class="tmb-merged-modal-meta">' + escapeHtml_(slot) +
            (meta.length ? ' · ' + escapeHtml_(meta.join(' · ')) : '') +
          '</span>' +
        '</div>' +
        '<div class="tmb-merged-modal-body">' +
          wodCardHtml +
          '<div class="tmb-merged-desc-block">' +
            (descHtml || (wodCardHtml
              ? '<p class="tmb-merged-modal-empty">No extra notes. See the prescription above.</p>'
              : '<p class="tmb-merged-modal-empty">No description yet for this session.</p>')) +
          '</div>' +
          setsHtml +
          sessionTotalsHtml +
          notesHtml +
          photoHtml +
          videoHtml +
        '</div>' +
        '<div class="tmb-merged-modal-foot">' +
          (href ? '<a class="tmb-merged-source-link" href="' + escapeHtml_(href) + '">Open full plan page →</a>' : '') +
          '<button type="button" class="tmb-merged-modal-close-btn">Close</button>' +
          '<button type="button" class="tmb-merged-modal-save-btn">✓ Saved</button>' +
        '</div>' +
      '</div>';
    // Append to documentElement (html), NOT body. Body had a brief
    // sibling-transform race on click (.tmb-merged-card:active had a
    // 0.1s transform transition that confused fixed-position math).
    // Lock html scroll via a class on documentElement so the modal is
    // never a sibling of anything mid-transition.
    document.documentElement.classList.add('tmb-merged-modal-open');
    document.body.classList.add('tmb-merged-modal-open');
    document.documentElement.appendChild(overlay);
    // Force a synchronous layout commit before the first paint so the
    // modal lands centered immediately (no need for a hover/repaint to
    // kick it). Reading offsetHeight forces reflow.
    /* eslint-disable-next-line no-unused-expressions */
    overlay.offsetHeight;
    // Move focus into the modal so the browser doesn't paint a
    // :focus-visible ring on the role=dialog wrapper. Close button is
    // the safe choice, it's small, focus-ring shows on a normal control.
    try {
      var closeX = overlay.querySelector('.tmb-merged-modal-close');
      if (closeX) closeX.focus({ preventScroll: true });
    } catch (_e) {}

    // Dirty-guard for destructive UI exits. Stage any focused edit
    // first (blur fires the stageFieldChange_ path) so a user who hits
    // Close mid-typing isn't asked about a value they haven't actually
    // committed yet.
    function confirmDiscardIfDirty_() {
      try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (_e) {}
      var sess = overlay._tmbSession;
      if (!sess || !sess.dirty) return true;
      return window.confirm('You have unsaved changes in this session. Discard them?');
    }
    function closeModal(force) {
      if (!force && !confirmDiscardIfDirty_()) return;
      overlay.removeEventListener('click', onOverlayClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onBeforeUnload);
      overlay.remove();
      document.body.classList.remove('tmb-merged-modal-open');
      document.documentElement.classList.remove('tmb-merged-modal-open');
    }
    function onOverlayClick(ev) {
      if (ev.target === overlay) return closeModal();
      if (ev.target.closest('.tmb-merged-modal-close, .tmb-merged-modal-close-btn')) return closeModal();
      if (ev.target.closest('.tmb-merged-modal-save-btn')) {
        // Blur the focused input so its last edit stages, then commit
        // every dirty set in pendingSets via postMergedWriteback_('set').
        try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (_e) {}
        commitDirtySets_();
        return;
      }
      // Photo thumb → open in-modal lightbox so the photo doesn't yank
      // the user off to a new tab and break the dirty-guard flow.
      var thumb = ev.target.closest('[data-photo-open]');
      if (thumb) {
        ev.preventDefault();
        openPhotoLightbox_(thumb.getAttribute('data-photo-open') || '', 'photo');
        return;
      }
      var videoThumb = ev.target.closest('[data-video-open]');
      if (videoThumb) {
        ev.preventDefault();
        openPhotoLightbox_(videoThumb.getAttribute('data-video-open') || '', 'video');
      }
    }
    // Stack a lightbox above the modal (z-index 11000 > modal's 10000).
    // Click outside the media or press Escape to close. kind ∈
    // {'photo','video'}, video gets a <video controls autoplay> so
    // the athlete can review their movement without leaving the modal.
    function openPhotoLightbox_(url, kind) {
      if (!url) return;
      var lb = document.createElement('div');
      lb.className = 'tmb-merged-photo-lightbox';
      var media = (kind === 'video')
        ? '<video src="' + escapeAttr_(url) + '" controls autoplay playsinline></video>'
        : '<img src="' + escapeAttr_(url) + '" alt="">';
      lb.innerHTML =
        '<button type="button" class="tmb-merged-photo-lightbox-close" aria-label="Close">×</button>' +
        media;
      function close_() {
        lb.removeEventListener('click', onClick_);
        document.removeEventListener('keydown', onKey_);
        lb.remove();
      }
      function onClick_(ev) {
        if (ev.target.tagName === 'IMG' || ev.target.tagName === 'VIDEO') return;
        close_();
      }
      function onKey_(ev) {
        if (ev.key === 'Escape') { ev.stopPropagation(); close_(); }
      }
      lb.addEventListener('click', onClick_);
      document.addEventListener('keydown', onKey_, true);
      document.documentElement.appendChild(lb);
    }
    function onKey(ev) {
      if (ev.key === 'Escape') return closeModal();
      // Cmd+S / Ctrl+S → save (only when there are pending changes).
      // The browser's own save-page action gets cancelled in either
      // case so users don't accidentally save the page HTML.
      if (ev.key && (ev.key === 's' || ev.key === 'S') && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch (_e) {}
        if (overlay._tmbSession && overlay._tmbSession.dirty) commitDirtySets_();
      }
    }
    // Browser-level guard for tab close / refresh / back navigation
    // when the session is dirty. The standard returnValue dance, the
    // exact message is browser-controlled but the prompt fires.
    function onBeforeUnload(ev) {
      var sess = overlay._tmbSession;
      if (!sess || !sess.dirty) return undefined;
      ev.preventDefault();
      ev.returnValue = '';
      return '';
    }
    overlay.addEventListener('click', onOverlayClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onBeforeUnload);

    // Tick handler, optimistic write: flip local state instantly so
    // the UI feels snappy, then POST. On success the local stays as-is
    // (we already updated it). On failure, revert + show a small
    // error chip. The 60s background poll catches anything weirder.
    var tickEl = overlay.querySelector('[data-tick-input]');
    if (tickEl && tickPerson) {
      tickEl.addEventListener('change', function () {
        var done = tickEl.checked;
        var statusEl = overlay.querySelector('[data-tick-status]');
        if (statusEl) statusEl.textContent = 'Saving…';
        // Optimistic local update first.
        updateLocalExport_(entry.sourceToken, s.ID, tickPerson, {
          Done: done,
          Source: 'planned',
        });
        // Invalidate the merged-plan's sessionStorage cache for this
        // token so the next fetch picks up the canonical server state.
        try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
        postTickWriteback_(entry.sourceToken, s.ID, tickPerson, done, email, function (err, j) {
          if (err || !j || !j.ok) {
            // Revert.
            tickEl.checked = !done;
            updateLocalExport_(entry.sourceToken, s.ID, tickPerson, { Done: !done });
            if (statusEl) {
              statusEl.textContent = 'Failed, retry';
              statusEl.classList.add('is-error');
            }
            return;
          }
          if (statusEl) {
            statusEl.textContent = done ? 'Saved ✓' : 'Cleared';
            statusEl.classList.remove('is-error');
            setTimeout(function () { if (statusEl) statusEl.textContent = ''; }, 1500);
          }
        });
      });
    }

    // Stage 2, time blur-save. (#109 v1 stage 2)
    var timeEl = overlay.querySelector('[data-time-input]');
    if (timeEl && tickPerson) {
      timeEl.addEventListener('blur', function () {
        var raw = timeEl.value;
        var v = (raw === '' || raw == null) ? '' : Number(raw);
        var statusEl = overlay.querySelector('[data-time-status]');
        if (statusEl) { statusEl.textContent = 'Saving…'; statusEl.classList.remove('is-error'); }
        updateLocalExport_(entry.sourceToken, s.ID, tickPerson, { 'Actual (min)': v });
        try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
        postMergedWriteback_('time', entry.sourceToken, s.ID, tickPerson, { actualMin: v }, email, function (err, j) {
          if (err || !j || !j.ok) {
            if (statusEl) { statusEl.textContent = 'Failed, retry'; statusEl.classList.add('is-error'); }
            return;
          }
          if (statusEl) {
            statusEl.textContent = 'Saved ✓';
            setTimeout(function () { if (statusEl) statusEl.textContent = ''; }, 1500);
          }
        });
      });
    }

    // Stage 2, note blur-save. (#109 v1 stage 2)
    var noteEl = overlay.querySelector('[data-note-input]');
    if (noteEl && tickPerson) {
      noteEl.addEventListener('blur', function () {
        var v = String(noteEl.value || '');
        var statusEl = overlay.querySelector('[data-note-status]');
        if (statusEl) { statusEl.textContent = 'Saving…'; statusEl.classList.remove('is-error'); }
        updateLocalExport_(entry.sourceToken, s.ID, tickPerson, { Notes: v });
        try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
        postMergedWriteback_('note', entry.sourceToken, s.ID, tickPerson, { note: v }, email, function (err, j) {
          if (err || !j || !j.ok) {
            if (statusEl) { statusEl.textContent = 'Failed, retry'; statusEl.classList.add('is-error'); }
            return;
          }
          if (statusEl) {
            statusEl.textContent = 'Saved ✓';
            setTimeout(function () { if (statusEl) statusEl.textContent = ''; }, 1500);
          }
        });
      });
    }

    // Stage 3, photo upload. (#109 v1 stage 3)
    var photoInputEl = overlay.querySelector('[data-photo-input]');
    if (photoInputEl && tickPerson) {
      photoInputEl.addEventListener('change', function () {
        var file = photoInputEl.files && photoInputEl.files[0];
        if (!file) return;
        var statusEl = overlay.querySelector('[data-photos-status]');
        var stripEl  = overlay.querySelector('[data-photos-strip]');
        var countEl  = overlay.querySelector('[data-photos-count]');
        var addLabel = overlay.querySelector('.tmb-merged-photo-add');
        if (statusEl) { statusEl.textContent = 'Resizing…'; statusEl.classList.remove('is-error'); }
        imageFileToResizedDataUrl_(file, 1280, 0.80, function (resizeErr, dataUrl) {
          if (resizeErr) {
            if (statusEl) { statusEl.textContent = resizeErr.message || 'Resize failed'; statusEl.classList.add('is-error'); }
            photoInputEl.value = '';
            return;
          }
          if (statusEl) statusEl.textContent = 'Uploading…';
          postMergedWriteback_('photo', entry.sourceToken, s.ID, tickPerson, { dataUrl: dataUrl }, email, function (err, j) {
            photoInputEl.value = '';
            if (err || !j || !j.ok || !j.fileId) {
              if (statusEl) {
                statusEl.textContent = (j && j.error) ? ('Failed: ' + j.error) : 'Upload failed, retry';
                statusEl.classList.add('is-error');
              }
              return;
            }
            // Optimistic local + DOM update.
            appendLocalImageId_(entry.sourceToken, s.ID, tickPerson, j.fileId);
            try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
            if (stripEl && addLabel) {
              var url = photoProxyUrl_(entry.sourceToken, j.fileId);
              var thumb = document.createElement('button');
              thumb.type = 'button';
              thumb.className = 'tmb-merged-photo-thumb';
              thumb.setAttribute('data-photo-open', url);
              thumb.setAttribute('data-fid', j.fileId);
              thumb.setAttribute('aria-label', 'Open photo');
              thumb.innerHTML = '<img src="' + escapeAttr_(url) + '" loading="lazy" alt="">';
              stripEl.insertBefore(thumb, addLabel);
            }
            if (countEl) {
              var n = (Number(countEl.textContent) || 0) + 1;
              countEl.textContent = String(n);
            }
            if (statusEl) {
              statusEl.textContent = 'Saved ✓';
              setTimeout(function () { if (statusEl) statusEl.textContent = ''; }, 1500);
            }
          });
        });
      });
    }

    // Video upload, single-shot dataURL POST to the source plan's
    // 'video' handler. Capped at 7 MB raw because base64 inflation
    // pushes anything bigger past the doPost 10 MB ceiling. Larger
    // files need the chunked-resumable uploader on the per-plan side.
    var videoInputEl = overlay.querySelector('[data-video-input]');
    if (videoInputEl && tickPerson) {
      var MAX_VIDEO_BYTES = 7 * 1024 * 1024;
      videoInputEl.addEventListener('change', function () {
        var file = videoInputEl.files && videoInputEl.files[0];
        if (!file) return;
        var statusEl = overlay.querySelector('[data-videos-status]');
        var stripEl  = overlay.querySelector('[data-videos-strip]');
        var countEl  = overlay.querySelector('[data-videos-count]');
        var addLabel = overlay.querySelector('[data-videos-row] .tmb-merged-photo-add');
        function showErr_(msg) {
          if (statusEl) { statusEl.textContent = msg; statusEl.classList.add('is-error'); }
          videoInputEl.value = '';
        }
        if (file.size > MAX_VIDEO_BYTES) {
          showErr_('Video too large (' + Math.round(file.size / 1024 / 1024) + ' MB), max 7 MB.');
          return;
        }
        if (statusEl) { statusEl.textContent = 'Reading…'; statusEl.classList.remove('is-error'); }
        var reader = new FileReader();
        reader.onerror = function () { showErr_('Read failed, retry'); };
        reader.onload = function () {
          if (statusEl) statusEl.textContent = 'Uploading…';
          postMergedWriteback_('video', entry.sourceToken, s.ID, tickPerson, { dataUrl: reader.result }, email, function (err, j) {
            videoInputEl.value = '';
            if (err || !j || !j.ok || !j.fileId) {
              if (statusEl) {
                statusEl.textContent = (j && j.error) ? ('Failed: ' + j.error) : 'Upload failed, retry';
                statusEl.classList.add('is-error');
              }
              return;
            }
            appendLocalVideoId_(entry.sourceToken, s.ID, tickPerson, j.fileId);
            try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
            if (stripEl && addLabel) {
              var url = photoProxyUrl_(entry.sourceToken, j.fileId);
              var thumb = document.createElement('button');
              thumb.type = 'button';
              thumb.className = 'tmb-merged-photo-thumb tmb-merged-video-thumb';
              thumb.setAttribute('data-video-open', url);
              thumb.setAttribute('data-fid', j.fileId);
              thumb.setAttribute('aria-label', 'Play video');
              thumb.innerHTML = '<video src="' + escapeAttr_(url) + '" preload="metadata" muted playsinline></video>' +
                '<span class="tmb-merged-video-thumb-icon" aria-hidden="true">▶</span>';
              stripEl.insertBefore(thumb, addLabel);
            }
            if (countEl) {
              var n = (Number(countEl.textContent) || 0) + 1;
              countEl.textContent = String(n);
            }
            if (statusEl) {
              statusEl.textContent = 'Saved ✓';
              setTimeout(function () { if (statusEl) statusEl.textContent = ''; }, 1500);
            }
          });
        };
        reader.readAsDataURL(file);
      });
    }

    // Stage 4b, sets editor blur-save + delete + add. (#109 v1 stage 4b)
    var setsTableEl = overlay.querySelector('[data-sets-table]');
    // Save-button state machine. Mirrors per-plan v3 (training-plan-v2.js
    // updateSaveBtn_) so the merged modal feels identical:
    //   idle/saved → "✓ Saved"     (green outline, disabled)
    //   dirty       → "Save changes" (red filled, enabled)
    //   saving      → "Saving…"     (grey, disabled)
    //   error       → "Save failed. Retry" (red outline, enabled)
    function updateSaveBtn_(overlayEl) {
      var btn = overlayEl.querySelector('.tmb-merged-modal-save-btn');
      if (!btn) return;
      var sess = overlayEl._tmbSession || {};
      if (sess.saving) {
        btn.disabled = true;
        btn.dataset.state = 'saving';
        btn.textContent = 'Saving…';
        btn.title = '';
        return;
      }
      if (sess.lastError) {
        btn.disabled = false;
        btn.dataset.state = 'error';
        btn.textContent = 'Save failed. Retry';
        btn.title = sess.lastError;
        return;
      }
      if (sess.dirty) {
        btn.disabled = false;
        btn.dataset.state = 'dirty';
        btn.textContent = 'Save changes';
        btn.title = '';
        return;
      }
      btn.disabled = true;
      btn.dataset.state = 'idle';
      btn.textContent = '✓ Saved';
      btn.title = '';
    }

    function flashSavedBtn_() {
      var btn = overlay.querySelector('.tmb-merged-modal-save-btn');
      if (!btn) return;
      btn.disabled = true;
      btn.dataset.state = 'saved';
      btn.textContent = '✓ Saved';
      setTimeout(function () { updateSaveBtn_(overlay); }, 1500);
    }

    // Commit every staged change in pendingSets sequentially. Sequential
    // (not parallel) so a row that depends on an upsert seen earlier in
    // the queue doesn't race. On any failure: stop, surface the error
    // on the Save button + flash the offending row's border.
    function commitDirtySets_() {
      var sess = overlay._tmbSession;
      if (!sess || sess.saving) return;
      var nums = Object.keys(sess.pendingSets);
      if (!nums.length) { sess.dirty = false; updateSaveBtn_(overlay); return; }
      sess.saving = true;
      sess.lastError = null;
      updateSaveBtn_(overlay);
      var i = 0;
      function next_() {
        if (i >= nums.length) {
          sess.saving = false;
          sess.dirty = false;
          sess.pendingSets = {};
          try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
          flashSavedBtn_();
          return;
        }
        var setNumber = Number(nums[i]);
        var pending = sess.pendingSets[setNumber];
        postMergedWriteback_('set', entry.sourceToken, s.ID, tickPerson,
          Object.assign({ setNumber: setNumber }, pending.fields),
          email,
          function (err, j) {
            if (err || !j || !j.ok) {
              sess.saving = false;
              sess.lastError = (err && err.message) || (j && j.error) || 'Server returned an error.';
              var rowEl = setsTableEl && setsTableEl.querySelector('[data-set-num="' + setNumber + '"]');
              if (rowEl) {
                rowEl.classList.add('tmb-merged-editor-row--error');
                setTimeout(function () { rowEl.classList.remove('tmb-merged-editor-row--error'); }, 2500);
              }
              updateSaveBtn_(overlay);
              return;
            }
            // This set is clean now, drop it from pending so a retry
            // doesn't re-post a successful row.
            delete sess.pendingSets[setNumber];
            i++;
            next_();
          });
      }
      next_();
    }

    // Initial button paint, modal opens with nothing pending, so the
    // button should read "✓ Saved" from the get-go.
    updateSaveBtn_(overlay);

    if (setsTableEl && tickPerson) {
      // Field change → stage into pendingSets, mark dirty, refresh the
      // Save button. POST happens later when the user clicks Save (see
      // commitDirtySets_). Same shape as per-plan v3's session.dirty
      // model so the two modals feel identical.
      function stageFieldChange_(inp) {
        var rowEl = inp.closest('[data-set-num]');
        if (!rowEl) return;
        var setNumber = Number(rowEl.getAttribute('data-set-num') || 0);
        if (!setNumber) return;
        var field = inp.getAttribute('data-set-field');
        var raw = (inp.type === 'checkbox') ? inp.checked : inp.value;
        var pending = overlay._tmbSession.pendingSets[setNumber] ||
          (overlay._tmbSession.pendingSets[setNumber] = { fields: {}, localFields: {} });
        if (field === 'family')    { pending.fields.family   = String(raw || ''); pending.localFields.Family   = String(raw || ''); }
        if (field === 'sets')      { pending.fields.sets     = raw === '' ? '' : Number(raw); pending.localFields.Sets   = raw === '' ? '' : Number(raw); }
        if (field === 'reps')      { pending.fields.reps     = raw === '' ? '' : Number(raw); pending.localFields.Reps   = raw === '' ? '' : Number(raw); }
        if (field === 'weight')    { pending.fields.weight   = raw === '' ? '' : Number(raw); pending.localFields.Weight = raw === '' ? '' : Number(raw); }
        if (field === 'distance')  { pending.fields.distance = raw === '' ? '' : Number(raw); pending.localFields.Distance = raw === '' ? '' : Number(raw); }
        if (field === 'time')      { pending.fields.time     = String(raw || ''); pending.localFields.Time     = String(raw || ''); }
        if (field === 'pace')      { pending.fields.pace     = String(raw || ''); pending.localFields.Pace     = String(raw || ''); }
        if (field === 'calories')  { pending.fields.calories = raw === '' ? '' : Number(raw); pending.localFields.Calories = raw === '' ? '' : Number(raw); }
        if (field === 'rpe')       { pending.fields.rpe      = raw === '' ? '' : Number(raw); pending.localFields.RPE    = raw === '' ? '' : Number(raw); }
        if (field === 'completed') {
          pending.fields.completed = !!raw;
          pending.localFields.Completed = !!raw;
          rowEl.classList.toggle('is-done', !!raw);
        }
        // Optimistic local update so subsequent renders reflect the
        // staged value even before save.
        updateLocalSet_(entry.sourceToken, s.ID, tickPerson, setNumber, pending.localFields);
        rowEl.classList.remove('is-programmed');
        rowEl.classList.remove('tmb-merged-editor-row--error');
        overlay._tmbSession.dirty = true;
        overlay._tmbSession.lastError = null;
        updateSaveBtn_(overlay);
      }

      setsTableEl.addEventListener('blur', function (ev) {
        var inp = ev.target.closest('[data-set-field]');
        if (!inp) return;
        var field = inp.getAttribute('data-set-field');
        stageFieldChange_(inp);
        // If the family changed, the row's metric_type may have shifted,
        // re-evaluate which columns apply for this movement and grey
        // the ones that don't.
        if (field === 'family') {
          refreshRowMetricMask_(inp.closest('[data-set-num]'), plan);
        }
        // Pace triad, if the user just edited distance / time / pace
        // and the other two are present, derive the third and stage it
        // so it saves alongside the user's edit.
        if (field === 'distance' || field === 'time' || field === 'pace') {
          var rowEl = inp.closest('[data-set-num]');
          var derived = deriveTriadFromInputs_(rowEl, field);
          if (derived) {
            var derivedInp = rowEl.querySelector('[data-set-field="' + derived.field + '"]');
            if (derivedInp) {
              derivedInp.value = derived.value;
              stageFieldChange_(derivedInp);
              derivedInp.classList.add('tmb-merged-editor-input--derived');
              setTimeout(function () {
                derivedInp.classList.remove('tmb-merged-editor-input--derived');
              }, 800);
            }
          }
        }
      }, true);  // useCapture so blur fires on the table
      // Live re-mask while the user types in Family, picking a movement
      // from the datalist fires `input` immediately, so the row adapts
      // before blur. Slightly noisier than blur but feels responsive.
      setsTableEl.addEventListener('input', function (ev) {
        var inp = ev.target.closest('[data-set-field="family"]');
        if (!inp) return;
        refreshRowMetricMask_(inp.closest('[data-set-num]'), plan);
      });

      // Checkbox toggles stage immediately, they don't naturally blur
      // after a click and users expect the tick to feel atomic.
      setsTableEl.addEventListener('change', function (ev) {
        var inp = ev.target.closest('[data-set-field="completed"]');
        if (!inp) return;
        stageFieldChange_(inp);
      });

      // × delete row.
      setsTableEl.addEventListener('click', function (ev) {
        var btn = ev.target.closest('[data-set-delete]');
        if (!btn) return;
        var setNumber = Number(btn.getAttribute('data-set-delete') || 0);
        if (!setNumber) return;
        var rowEl = btn.closest('.tmb-merged-editor-row');
        if (rowEl) rowEl.style.opacity = '0.4';
        postMergedWriteback_('setDelete', entry.sourceToken, s.ID, tickPerson,
          { setNumber: setNumber }, email, function (err, j) {
            if (err || !j || !j.ok) {
              if (rowEl) {
                rowEl.style.opacity = '';
                rowEl.classList.add('tmb-merged-editor-row--error');
                setTimeout(function () { rowEl.classList.remove('tmb-merged-editor-row--error'); }, 2000);
              }
              return;
            }
            removeLocalSet_(entry.sourceToken, s.ID, tickPerson, setNumber);
            try { sessionStorage.removeItem(PLAN_DATA_CACHE_KEY_ + entry.sourceToken); } catch (_e) {}
            if (rowEl) rowEl.remove();
          });
      });
    }

    // + Add Set, append a blank row locally. First blur on any field
    // allocates the row server-side at the next Set number.
    var addSetEl = overlay.querySelector('[data-set-add]');
    if (addSetEl && tickPerson && setsTableEl) {
      addSetEl.addEventListener('click', function () {
        var n = nextSetNumber_(setsTableEl);
        var blankRow = {
          setNumber: n,
          family: '', sets: '', reps: '', weight: '', distance: '',
          time: '', pace: '', calories: '', rpe: '',
          completed: false,
          isProgrammedOnly: false,
        };
        var tmp = document.createElement('div');
        tmp.innerHTML = buildEditableSetRow_(blankRow, plan);
        var newRowEl = tmp.firstChild;
        setsTableEl.appendChild(newRowEl);
        // Focus the new row's Movement input so the user can start typing.
        var firstInput = newRowEl.querySelector('[data-set-field="family"]');
        if (firstInput) firstInput.focus();
      });
    }
  }

  // Card-click delegation. Single listener on resultsEl survives every
  // re-render and finds the entry via the registry.
  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest && ev.target.closest('[data-merged-entry]');
    if (!btn) return;
    var eid = btn.getAttribute('data-merged-entry') || '';
    var entry = __entryRegistry_[eid];
    if (!entry) return;
    ev.preventDefault();
    openSessionModal_(entry, root && root.__hubEmail || '');
  });

  function renderDay_(iso, entries, email) {
    var d = new Date(iso + 'T00:00:00');
    var todayCls = iso === todayIso_() ? ' tmb-merged-day--today' : '';
    var label = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });
    // Sort: AM → All day → PM → Focus
    entries.sort(function (a, b) {
      var sa = SLOT_ORDER[String(a.session.Slot || 'all day').toLowerCase()] || 5;
      var sb = SLOT_ORDER[String(b.session.Slot || 'all day').toLowerCase()] || 5;
      if (sa !== sb) return sa - sb;
      var pa = String(a.session.Priority || 'P3');
      var pb = String(b.session.Priority || 'P3');
      return pa < pb ? -1 : pa > pb ? 1 : 0;
    });
    var cards = entries.map(function (e) { return renderSessionCard_(e, email); }).join('');
    return '<div class="tmb-merged-day' + todayCls + '">' +
      '<div class="tmb-merged-day-head"><span class="tmb-merged-day-label">' + escapeHtml_(label) + '</span>' +
        (iso === todayIso_() ? '<span class="tmb-merged-day-today-tag">TODAY</span>' : '') +
      '</div>' +
      '<div class="tmb-merged-day-cards">' + cards + '</div>' +
    '</div>';
  }

  function renderWeek_(weekStart, sessionsByDate, email) {
    var html = '<section class="tmb-merged-week">';
    var hadAny = false;
    for (var i = 0; i < 7; i++) {
      var iso = isoDateAddDays_(weekStart, i);
      var entries = sessionsByDate[iso] || [];
      if (!entries.length) continue;
      hadAny = true;
      html += renderDay_(iso, entries, email);
    }
    if (!hadAny) {
      html += '<div class="tmb-merged-empty">No sessions scheduled in this week across your ticked plans.</div>';
    }
    html += '</section>';
    return html;
  }

  // Horizontal strip of 14 day buttons (today + 13 ahead), matches
  // the merged-page windowing range so every day button has data.
  // Click jumps the active day; the "TODAY" pill is the snap-back.
  var DOW_SHORT_ = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function renderDayStrip_(days, activeIso) {
    var today = todayIso_();
    return '<nav class="tmb-merged-weeks" data-merged-days>' +
      days.map(function (iso) {
        var d = new Date(iso + 'T00:00:00');
        var dow = DOW_SHORT_[d.getDay()];
        var date = String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
        var cls = 'tmb-merged-week-btn' +
          (iso === activeIso ? ' is-active' : '') +
          (iso === today ? ' is-today' : '');
        return '<button type="button" class="' + cls + '" data-merged-day="' + iso + '">' +
          dow +
          '<span class="tmb-merged-week-btn-date">' + date + '</span>' +
        '</button>';
      }).join('') +
    '</nav>';
  }

  // 14 days from today, matches MERGED_WINDOW_DAYS_.
  function buildDayList_() {
    var days = [];
    var today = todayIso_();
    for (var i = 0; i < 14; i++) days.push(isoDateAddDays_(today, i));
    return days;
  }

  // Legacy week-list kept for back-compat with any caller that hasn't
  // migrated yet, points at the new day list.
  function buildWeekList_() {
    var startsSeen = {};
    var today = todayIso_();
    var thisWeekStart = weekStartFor_(today);
    var weeks = [thisWeekStart];
    startsSeen[thisWeekStart] = true;
    for (var i = 1; i <= 11; i++) {
      var ws = isoDateAddDays_(thisWeekStart, i * 7);
      weeks.push(ws);
      startsSeen[ws] = true;
    }
    return weeks;
  }

  // Render a single day panel, mirrors the per-plan view's TODAY card
  // shape: eyebrow (TODAY/upcoming/past) + date heading + meta + stacked
  // session cards. Prev/next arrows live on either side of the panel.
  function renderDayPanel_(iso, byDate, email) {
    var entries = byDate[iso] || [];
    var d = new Date(iso + 'T00:00:00');
    var today = todayIso_();
    var eyebrow = iso === today ? 'TODAY' :
      iso < today ? 'PAST' : 'UPCOMING';
    var dateLabel = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
    var todayCls = iso === today ? ' tmb-merged-day--today' : '';
    var cards;
    if (!entries.length) {
      cards = '<div class="tmb-merged-day-empty">No sessions scheduled this day across your ticked plans.</div>';
    } else {
      // Sort entries by priority within slot, then group by slot so we
      // can emit per-slot labels, mirrors the per-plan modal's renderDayCell
      // (AM / All day / PM / Focus labels between sessions). Sessions
      // within a slot still sort P1 → P5.
      entries.sort(function (a, b) {
        var sa = SLOT_ORDER[String(a.session.Slot || 'all day').toLowerCase()] || 5;
        var sb = SLOT_ORDER[String(b.session.Slot || 'all day').toLowerCase()] || 5;
        if (sa !== sb) return sa - sb;
        var pa = String(a.session.Priority || 'P3');
        var pb = String(b.session.Priority || 'P3');
        return pa < pb ? -1 : pa > pb ? 1 : 0;
      });
      var bySlot = {};
      var slotsSeen = [];
      entries.forEach(function (e) {
        var raw = String(e.session.Slot || 'All day').trim();
        var key = raw.toLowerCase();
        if (!bySlot[key]) { bySlot[key] = { label: raw, items: [] }; slotsSeen.push(key); }
        bySlot[key].items.push(e);
      });
      // Canonical slot order; any unrecognised slot strings sink to the end.
      var SLOT_KEY_ORDER = ['am', 'all day', 'pm', 'focus'];
      var ordered = SLOT_KEY_ORDER.filter(function (k) { return bySlot[k]; })
        .concat(slotsSeen.filter(function (k) { return SLOT_KEY_ORDER.indexOf(k) === -1; }));
      var pieces = [];
      ordered.forEach(function (k) {
        var bucket = bySlot[k];
        var label = bucket.label;
        // Normalise canonical labels to uppercase mono; preserve trainer-
        // typed extras (e.g. "Optional", "Bonus") as-given.
        if (SLOT_KEY_ORDER.indexOf(k) !== -1) {
          label = (k === 'all day') ? 'All day' : k.toUpperCase();
        }
        pieces.push('<div class="tmb-merged-slot-label">' + escapeHtml_(label) + '</div>');
        bucket.items.forEach(function (e) {
          pieces.push(renderSessionCard_(e, email));
        });
      });
      cards = pieces.join('');
    }
    var snapBtn = (iso !== today)
      ? '<button type="button" class="tmb-merged-today-btn" data-merged-today aria-label="Jump to today">↺ Today</button>'
      : '';
    // Centered eyebrow + date heading, matching the per-plan today-nav-center.
    return '<div class="tmb-merged-day-wrap">' +
      '<button type="button" class="tmb-merged-daynav tmb-merged-daynav--prev" data-merged-daynav="prev" aria-label="Previous day">←</button>' +
      '<div class="tmb-merged-day' + todayCls + '">' +
        '<div class="tmb-merged-day-nav-center">' +
          '<div class="tmb-merged-day-eyebrow">' + escapeHtml_(eyebrow) + '</div>' +
          '<div class="tmb-merged-day-title">' +
            '<span class="tmb-merged-day-label">' + escapeHtml_(dateLabel) + '</span>' +
            (snapBtn ? ' ' + snapBtn : '') +
          '</div>' +
        '</div>' +
        '<div class="tmb-merged-day-cards">' + cards + '</div>' +
      '</div>' +
      '<button type="button" class="tmb-merged-daynav tmb-merged-daynav--next" data-merged-daynav="next" aria-label="Next day">→</button>' +
    '</div>';
  }

  function render_(entriesByToken, email, planMetas) {
    // Stash the email on root so the modal click handler can build the
    // "Open in source plan" link without re-reading localStorage.
    if (root) root.__hubEmail = email;

    // Local filter state, mirrors localStorage but lets chip clicks
    // toggle inclusion without re-fetching.
    var filter = readPlanFilter_();

    function flattenEnabled() {
      var out = [];
      planMetas.forEach(function (p) {
        if (!planIsIncluded_(p.token, filter)) return;
        var entries = entriesByToken[p.token] || [];
        for (var i = 0; i < entries.length; i++) out.push(entries[i]);
      });
      return out;
    }

    function activeWeekIdx(activeDayIso, weeks) {
      var ws = weekStartFor_(activeDayIso);
      var i = weeks.indexOf(ws);
      return i >= 0 ? i : 0;
    }

    function renderChips() {
      return planMetas.map(function (p) {
        var enabled = planIsIncluded_(p.token, filter);
        return '<button type="button" class="tmb-merged-plan-pill' + (enabled ? ' is-on' : ' is-off') + '" ' +
          'data-merged-chip="' + escapeHtml_(p.token) + '" ' +
          'style="--plan-color: ' + planColor_(p.planTitle) + '" ' +
          'aria-pressed="' + (enabled ? 'true' : 'false') + '">' +
          '<span class="tmb-merged-plan-pill-dot"></span>' +
          '<span class="tmb-merged-plan-pill-state" aria-hidden="true">' + (enabled ? '✓' : '✕') + '</span>' +
          '<span class="tmb-merged-plan-pill-name">' + escapeHtml_(p.planTitle) + '</span>' +
        '</button>';
      }).join('');
    }

    // Active day starts as TODAY. Prev/next arrows move by 1 day; the
    // week strip below jumps to the Monday of the picked week.
    var activeDayIso = todayIso_();

    function rerender() {
      var enabled = flattenEnabled();
      var byDate = {};
      enabled.forEach(function (e) {
        if (!byDate[e.iso]) byDate[e.iso] = [];
        byDate[e.iso].push(e);
      });
      var days = buildDayList_();
      var anyEnabled = planMetas.some(function (p) { return planIsIncluded_(p.token, filter); });
      var body = anyEnabled
        ? (renderDayPanel_(activeDayIso, byDate, email) +
           renderDayStrip_(days, activeDayIso) +
           renderRandomWodPicker_())
        : '<div class="tmb-merged-empty">No plans selected. Tick a plan above to merge it in.</div>';
      resultsEl.innerHTML =
        '<div class="tmb-merged-plans" data-merged-chips>' + renderChips() + '</div>' + body;
    }
    rerender();

    // Random-WOD picker chip toggle, saves to localStorage + live-
    // updates the eligible count. Lives on resultsEl so it survives
    // every rerender.
    resultsEl.addEventListener('change', function (ev) {
      var input = ev.target && ev.target.closest && ev.target.closest('[data-wod-equip]');
      if (!input) return;
      var picker = input.closest('.tmb-merged-wod-picker');
      if (!picker) return;
      var key = input.getAttribute('data-wod-equip');
      var map = readPickerEquipment_();
      map[key] = !!input.checked;
      writePickerEquipment_(map);
      if (input.parentElement) input.parentElement.classList.toggle('is-on', !!input.checked);
      var count = picker.querySelector('[data-wod-picker-count]');
      if (count) count.textContent = wodsEligibleFor_(aggregateWodsAcrossPlans_(), map).length + ' eligible';
    });

    resultsEl.addEventListener('click', function (ev) {
      // Random-WOD roll button.
      var rollBtn = ev.target.closest && ev.target.closest('[data-wod-picker-roll]');
      if (rollBtn) {
        var picker = rollBtn.closest('.tmb-merged-wod-picker');
        if (!picker) return;
        var map = readPickerEquipment_();
        var allWods = aggregateWodsAcrossPlans_();
        var eligible = wodsEligibleFor_(allWods, map);
        var resultEl = picker.querySelector('[data-wod-picker-result]');
        if (!resultEl) return;
        if (!eligible.length) {
          resultEl.innerHTML = '<p class="tmb-merged-wod-picker-empty">No WODs match the equipment you have ticked. Tick more boxes and roll again.</p>';
          return;
        }
        var pick = eligible[Math.floor(Math.random() * eligible.length)];
        resultEl.innerHTML = renderWodCardFromWod_(pick);
        return;
      }
      // Chip toggle.
      var chip = ev.target.closest('[data-merged-chip]');
      if (chip) {
        var tok = chip.getAttribute('data-merged-chip') || '';
        if (!tok) return;
        filter[tok] = !planIsIncluded_(tok, filter);
        writePlanFilter_(filter);
        rerender();
        return;
      }
      // Snap-to-today button.
      if (ev.target.closest('[data-merged-today]')) {
        activeDayIso = todayIso_();
        rerender();
        return;
      }
      // Day button: jump directly to that ISO date.
      var dayBtn = ev.target.closest('[data-merged-day]');
      if (dayBtn) {
        var iso = dayBtn.getAttribute('data-merged-day');
        if (iso && iso !== activeDayIso) {
          activeDayIso = iso;
          rerender();
        }
        return;
      }
      // Day-nav arrow: ±1 day.
      var navBtn = ev.target.closest('[data-merged-daynav]');
      if (navBtn) {
        var dir = navBtn.getAttribute('data-merged-daynav') === 'next' ? 1 : -1;
        activeDayIso = isoDateAddDays_(activeDayIso, dir);
        rerender();
        return;
      }
    });

    // Keyboard ← / → also navigate.
    document.addEventListener('keydown', function (ev) {
      if (!resultsEl.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (ev.key === 'ArrowLeft')  { activeDayIso = isoDateAddDays_(activeDayIso, -1); rerender(); }
      else if (ev.key === 'ArrowRight') { activeDayIso = isoDateAddDays_(activeDayIso, 1); rerender(); }
    });
  }

  // Short-lived session cache for lookupAccess, prevents Apps Script
  // from rate-limiting back-navigation that re-runs the lookup within
  // a few seconds. 5-minute TTL is fine since the plan list doesn't
  // change often, and the per-plan data is fetched fresh each time.
  // #110, shared with hub + plan-page chip
  var LOOKUP_CACHE_KEY = 'tmb_lookup_cache';
  function readCachedLookup_(email) {
    try {
      var raw = sessionStorage.getItem(LOOKUP_CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || c.email !== email) return null;
      if (Date.now() - (c.at || 0) > 5 * 60 * 1000) return null;
      return c.data;
    } catch (_e) { return null; }
  }
  function writeCachedLookup_(email, data) {
    try { sessionStorage.setItem(LOOKUP_CACHE_KEY, JSON.stringify({ email: email, at: Date.now(), data: data })); }
    catch (_e) {}
  }

  function renderRetryError_(msg) {
    setStatus_(msg, 'error');
    if (resultsEl) {
      resultsEl.innerHTML = '<div class="tmb-merged-retry">' +
        '<button type="button" class="tmb-merged-retry-btn" data-merged-retry>Retry</button> ' +
        '<a class="tmb-merged-retry-link" href="/pages/training-plans">or open the plans hub</a>' +
      '</div>';
    }
  }

  function boot_() {
    var email = readSavedEmail_();
    if (!email) {
      setStatus_('No email saved yet. Visit /pages/training-plans first to look up your plans.', 'error');
      titleEl.textContent = 'Merged plan';
      subEl.textContent = '';
      return;
    }
    if (!endpoint) {
      setStatus_('Endpoint not configured. Ask the site admin.', 'error');
      return;
    }
    subEl.textContent = 'Loading your plans…';
    setStatus_('', '');

    // sessionStorage cache short-circuits back-navigation re-lookup.
    var cached = readCachedLookup_(email);
    var lookupPromise = cached
      ? Promise.resolve(cached)
      : fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({ action: 'lookupAccess', email: email }),
        }).then(function (r) { return r.json(); });

    lookupPromise
      .then(function (j) {
        if (!j || !j.ok || !j.plans || !j.plans.length) {
          renderRetryError_('No plans found for ' + email + '. Apps Script may be cold or throttling. Retry usually works.');
          subEl.textContent = '';
          return;
        }
        // Only cache valid responses to avoid sticking a bad-state empty.
        if (!cached) writeCachedLookup_(email, j);
        // Load EVERY plan with a pageUrl, chips toggle visibility at
        // render-time so users can tick/cross on the merged page itself
        // without a refetch.
        var plans = j.plans.filter(function (p) { return !!p.pageUrl; });
        if (!plans.length) {
          setStatus_('No plans found for ' + email + '.', 'error');
          subEl.textContent = '';
          return;
        }
        // Header, use trainer email's first name if we know it, else email.
        var firstPlan = j.plans[0] || {};
        titleEl.textContent = 'Plan · ' + (firstPlan.memberName || (email.split('@')[0]));
        subEl.textContent = plans.length + ' plans · ' + email;

        // Progress counter, update the status as plans land so the
        // user can see something is happening (Apps Script cold-start
        // can take 5-10s per plan).
        var loadedCount = 0;
        var totalToLoad = plans.length;
        setStatus_('Loading 0/' + totalToLoad + ' plans…', '');
        fetchPlansLimited_(plans, function (p) {
          var token = tokenFromPageUrl_(p.pageUrl);
          return fetchPlanData_(token, email).then(function (data) {
            loadedCount++;
            setStatus_('Loading ' + loadedCount + '/' + totalToLoad + ' plans…', '');
            return { meta: p, token: token, data: data };
          });
        }).then(function (results) {
          // One retry pass for failures, sequential, now the server is warm.
          var chain = Promise.resolve();
          results.forEach(function (r) {
            if (r.data) return;
            chain = chain.then(function () {
              setStatus_('Retrying ' + (r.meta.planTitle || 'plan') + '…', '');
              return fetchPlanData_(r.token, email).then(function (data) { if (data) r.data = data; });
            });
          });
          return chain.then(function () { return results; });
        }).then(function (results) {
          var loaded = results.filter(function (r) { return !!r.data; });
          if (!loaded.length) {
            setStatus_('Couldn\'t load any plan data. Try a hard refresh.', 'error');
            return;
          }
          // Surface the partial-load case so the subtitle's "N plans"
          // doesn't read as a lie when one plan timed out / cold-started.
          var failedCount = results.length - loaded.length;
          if (failedCount > 0) {
            setStatus_(failedCount + ' of ' + results.length + ' plans failed to load. They\'re still listed below but have no sessions yet. Refresh to retry.', 'error');
          } else {
            setStatus_('', '');
          }
          var entriesByToken = {};
          var planMetas = [];
          // Include EVERY plan from lookupAccess (loaded or not) so the
          // filter pills always match the subtitle's count. Failed plans
          // simply have no entries, their pill is still tickable and a
          // retry / re-render will pick them up if the data arrives.
          results.forEach(function (r) {
            planMetas.push({ planTitle: r.meta.planTitle, token: r.token, pageUrl: r.meta.pageUrl });
            if (r.data) {
              _planByToken_[r.token] = r.data;
              entriesByToken[r.token] = expandPlanSessions_(r.data, {
                token: r.token,
                planTitle: r.meta.planTitle || 'Plan',
                pageUrl: r.meta.pageUrl,
              });
            } else {
              entriesByToken[r.token] = [];
            }
          });
          render_(entriesByToken, email, planMetas);
        });
      })
      .catch(function (err) {
        try { window.tmbLogError && window.tmbLogError('fetch_lookupaccess_merged', String(err && err.message || err), { stack: err && err.stack || '', email: email }); } catch (_e) {}
        renderRetryError_('Network error reaching the plan endpoint. Tap Retry.');
        subEl.textContent = '';
      });
  }

  // Retry button binding, global delegation so it survives re-renders.
  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest && ev.target.closest('[data-merged-retry]');
    if (!btn) return;
    ev.preventDefault();
    // Wipe the cached (possibly-empty) lookup so the retry hits the
    // network fresh.
    try { sessionStorage.removeItem(LOOKUP_CACHE_KEY); } catch (_e) {}
    if (resultsEl) resultsEl.innerHTML = '';
    setStatus_('Retrying…', '');
    boot_();
  });

  boot_();
})();
